import React, { useState } from 'react';

// Complete RFE/NOID Response Agent Flow
export const RfeNoidResponseAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    noticeType: '',
    noticeDetails: {},
    uploadedNotice: null,
    parsedNoticeData: {},
    documents: [],
    evidenceStrategy: {},
    analysisResults: {},
    gapAnalysis: {},
    affidavits: [],
    coverLetter: '',
    finalReview: {},
    qualityAssurance: {},
    responsePacket: {},
    deadline: null,
    finalScore: 0
  });

  const steps = [
    'Intake & Type Identification',
    'Smart Breakdown of USCIS Request',
    'Document Upload Portal',
    'Evidence Strategy Session',
    'AI Document Analyzer',
    'Evidence Gap Analysis & Prioritization',
    'Additional Document Upload',
    'Affidavit Builder',
    'Drafting Cover Letter / Response',
    'AI Final Review & Checklist',
    'Final Quality Assurance',
    'Generate Response Packet'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">📄</span>
              <div>
                <h2 className="text-2xl font-bold text-white">RFE / NOID Response Agent</h2>
                <p className="text-gray-400">USCIS Notice Response Process</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Deadline Alert */}
          {formData.deadline && (
            <div className="mb-6 bg-red-900/20 border border-red-400 p-4 rounded-lg">
              <div className="flex items-center">
                <span className="text-red-400 mr-2">⏰</span>
                <span className="text-red-300 font-medium">Response Deadline:</span>
                <span className="text-white ml-2">{formData.deadline}</span>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gray-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Step Content */}
          {currentStep === 0 && (
            <RfeStep1Intake 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <RfeStep2Breakdown 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <RfeStep3DocumentUpload 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <RfeStep4EvidenceStrategy 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <RfeStep5DocumentAnalyzer 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <RfeStep6GapAnalysis 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <RfeStep7AdditionalDocuments 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 7 && (
            <RfeStep8AffidavitBuilder 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 8 && (
            <RfeStep9CoverLetter 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 9 && (
            <RfeStep10AIFinalReview 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 10 && (
            <RfeStep11QualityAssurance 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 11 && (
            <RfeStep12GeneratePacket 
              formData={formData} 
              setFormData={setFormData} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// RFE Step 1: Intake & Type Identification
const RfeStep1Intake = ({ formData, setFormData, onNext }) => {
  const [uploadedNotice, setUploadedNotice] = useState(formData.uploadedNotice || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [parsedData, setParsedData] = useState(formData.parsedNoticeData || {});

  const handleFileUpload = async (file) => {
    setUploadedNotice(file);
    setAnalyzing(true);

    // Dynamic AI parsing based on actual file content
    try {
      // Create FileReader to extract text content
      const fileText = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // Enhanced dynamic parsing algorithm
      const dynamicParsedData = {
        noticeType: extractNoticeType(fileText, file.name),
        caseNumber: extractCaseNumber(fileText, file.name),
        receiptNumber: extractReceiptNumber(fileText, file.name),
        processingOffice: extractProcessingOffice(fileText),
        deadline: extractDeadline(fileText),
        severity: calculateSeverity(fileText),
        mainIssues: extractMainIssues(fileText),
        extractedText: fileText.substring(0, 500) + (fileText.length > 500 ? '...' : ''),
        fileSize: file.size,
        fileName: file.name,
        analysisDate: new Date().toISOString()
      };
      
      setParsedData(dynamicParsedData);
      setFormData({
        ...formData,
        uploadedNotice: file,
        parsedNoticeData: dynamicParsedData,
        deadline: dynamicParsedData.deadline
      });
    } catch (error) {
      console.error('Error parsing document:', error);
      // Fallback analysis for binary files or parsing errors
      const fallbackData = {
        noticeType: determineNoticeTypeFromFilename(file.name),
        caseNumber: 'Unable to extract - please verify manually',
        receiptNumber: 'Unable to extract - please verify manually',
        processingOffice: 'Unable to extract from file',
        deadline: 'Unable to extract - check original notice',
        severity: 'Unknown - requires manual review',
        mainIssues: ['Please manually review uploaded notice for specific requirements'],
        extractedText: `Unable to parse ${file.type} file. File uploaded successfully.`,
        fileSize: file.size,
        fileName: file.name,
        analysisDate: new Date().toISOString(),
        parsingError: true
      };
      
      setParsedData(fallbackData);
      setFormData({
        ...formData,
        uploadedNotice: file,
        parsedNoticeData: fallbackData,
        deadline: null
      });
    }
    
    setAnalyzing(false);
  };

  // Dynamic extraction functions
  const extractNoticeType = (text, fileName) => {
    const textLower = text.toLowerCase();
    const fileNameLower = fileName.toLowerCase();
    
    if (textLower.includes('request for evidence') || textLower.includes('rfe') || fileNameLower.includes('rfe')) {
      return 'RFE';
    } else if (textLower.includes('notice of intent to deny') || textLower.includes('noid') || fileNameLower.includes('noid')) {
      return 'NOID';
    } else if (textLower.includes('notice of intent to revoke') || textLower.includes('noir')) {
      return 'NOIR';
    } else if (textLower.includes('notice to appear') || textLower.includes('nta')) {
      return 'NTA';
    } else if (textLower.includes('denial notice')) {
      return 'Denial Notice';
    }
    return 'Unknown Notice Type';
  };

  const extractCaseNumber = (text, fileName) => {
    // Look for various case number patterns
    const casePatterns = [
      /MSC\d{10}/gi,
      /EAC\d{10}/gi,
      /WAC\d{10}/gi,
      /NBC\d{10}/gi,
      /YSC\d{10}/gi,
      /IOE\d{10}/gi,
      /FDL\d{10}/gi,
      /LIN\d{10}/gi,
      /SRC\d{10}/gi,
      /VSC\d{10}/gi,
      /Case Number[:\s]+([A-Z]{3}\d{10})/gi,
      /Receipt Number[:\s]+([A-Z]{3}\d{10})/gi
    ];

    for (const pattern of casePatterns) {
      const match = text.match(pattern);
      if (match) {
        return match[0];
      }
    }

    // Try filename
    const fileMatch = fileName.match(/[A-Z]{3}\d{10}/gi);
    if (fileMatch) {
      return fileMatch[0];
    }

    return 'Case number not found in document';
  };

  const extractReceiptNumber = (text, fileName) => {
    // Often same as case number, but check for specific "Receipt Number" mentions
    const receiptPatterns = [
      /Receipt Number[:\s]+([A-Z]{3}\d{10})/gi,
      /Receipt[:\s]+([A-Z]{3}\d{10})/gi
    ];

    for (const pattern of receiptPatterns) {
      const match = text.match(pattern);
      if (match) {
        return match[1];
      }
    }

    // Fallback to case number extraction
    return extractCaseNumber(text, fileName);
  };

  const extractProcessingOffice = (text) => {
    const officePatterns = [
      /National Benefits Center/gi,
      /Nebraska Service Center/gi,
      /Texas Service Center/gi,
      /Vermont Service Center/gi,
      /California Service Center/gi,
      /Potomac Service Center/gi,
      /National Passport Center/gi,
      /([A-Z][a-z]+ Service Center)/gi,
      /([A-Z][a-z]+ Benefits Center)/gi,
      /USCIS ([A-Z][a-z]+ Office)/gi
    ];

    for (const pattern of officePatterns) {
      const match = text.match(pattern);
      if (match) {
        return match[0];
      }
    }

    return 'Processing office not identified';
  };

  const extractDeadline = (text) => {
    // Look for various date patterns
    const datePatterns = [
      /within (\d{1,3}) days/gi,
      /by ([A-Z][a-z]+ \d{1,2}, \d{4})/gi,
      /before ([A-Z][a-z]+ \d{1,2}, \d{4})/gi,
      /no later than ([A-Z][a-z]+ \d{1,2}, \d{4})/gi,
      /deadline[:\s]+([A-Z][a-z]+ \d{1,2}, \d{4})/gi,
      /(\d{1,2}\/\d{1,2}\/\d{4})/gi,
      /(\d{4}-\d{2}-\d{2})/gi
    ];

    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        if (match[1]) {
          return match[1];
        } else {
          return match[0];
        }
      }
    }

    return 'Deadline not found - check original notice';
  };

  const calculateSeverity = (text) => {
    const textLower = text.toLowerCase();
    let severityScore = 0;

    // High severity indicators
    if (textLower.includes('intent to deny') || textLower.includes('noid')) {
      severityScore += 3;
    }
    if (textLower.includes('final determination') || textLower.includes('last opportunity')) {
      severityScore += 2;
    }
    if (textLower.includes('insufficient evidence') || textLower.includes('failure to establish')) {
      severityScore += 2;
    }

    // Medium severity indicators
    if (textLower.includes('additional evidence') || textLower.includes('more information')) {
      severityScore += 1;
    }
    if (textLower.includes('clarification') || textLower.includes('explanation')) {
      severityScore += 1;
    }

    if (severityScore >= 4) return 'High';
    if (severityScore >= 2) return 'Medium';
    return 'Low';
  };

  const extractMainIssues = (text) => {
    const issues = [];
    const textLower = text.toLowerCase();

    // Work/Employment visa issues
    if (textLower.includes('specialty occupation') || textLower.includes('h-1b') || textLower.includes('employment')) {
      issues.push('Specialty occupation qualification requires evidence');
    }
    if (textLower.includes('degree requirement') || textLower.includes('bachelor') || textLower.includes('education')) {
      issues.push('Educational qualifications need documentation');
    }
    if (textLower.includes('employer-employee') || textLower.includes('job duties') || textLower.includes('position')) {
      issues.push('Employment relationship documentation needed');
    }
    if (textLower.includes('prevailing wage') || textLower.includes('salary') || textLower.includes('compensation')) {
      issues.push('Wage and compensation documentation required');
    }

    // Family-based/Marriage visa issues
    if (textLower.includes('bona fide') && (textLower.includes('marriage') || textLower.includes('relationship'))) {
      issues.push('Bona fide marriage evidence requires strengthening');
    }
    if (textLower.includes('joint') && textLower.includes('account')) {
      issues.push('Joint financial accounts evidence needed');
    }
    if (textLower.includes('cohabitation') || textLower.includes('living together')) {
      issues.push('Cohabitation evidence needed');
    }

    // Student visa issues
    if (textLower.includes('sevis') || textLower.includes('i-20') || textLower.includes('academic')) {
      issues.push('Academic status documentation required');
    }
    if (textLower.includes('full-time') && (textLower.includes('student') || textLower.includes('enrollment'))) {
      issues.push('Full-time enrollment evidence needed');
    }
    if (textLower.includes('opt') || textLower.includes('cpt') || textLower.includes('practical training')) {
      issues.push('Work authorization documentation required');
    }

    // Asylum/Protection issues
    if (textLower.includes('persecution') || textLower.includes('asylum') || textLower.includes('torture')) {
      issues.push('Persecution evidence requires documentation');
    }
    if (textLower.includes('country condition') || textLower.includes('human rights')) {
      issues.push('Country conditions evidence needed');
    }
    if (textLower.includes('political') && textLower.includes('opinion')) {
      issues.push('Political opinion persecution documentation required');
    }

    // Citizenship/Naturalization issues
    if (textLower.includes('continuous residence') || textLower.includes('physical presence')) {
      issues.push('Continuous residence documentation needed');
    }
    if (textLower.includes('good moral character') || textLower.includes('criminal')) {
      issues.push('Good moral character evidence required');
    }
    if (textLower.includes('english') || textLower.includes('civics') || textLower.includes('test')) {
      issues.push('Language or civics testing documentation needed');
    }

    // DACA/TPS issues
    if (textLower.includes('childhood arrival') || textLower.includes('daca')) {
      issues.push('Childhood arrival documentation required');
    }
    if (textLower.includes('temporary protected') || textLower.includes('tps')) {
      issues.push('TPS eligibility documentation needed');
    }

    // General issues applicable to all case types
    if (textLower.includes('financial') || textLower.includes('income') || textLower.includes('tax')) {
      issues.push('Financial documentation requires attention');
    }
    if (textLower.includes('birth certificate') || textLower.includes('identity')) {
      issues.push('Identity documentation issues identified');
    }
    if (textLower.includes('translation') || textLower.includes('certified')) {
      issues.push('Document translation or certification required');
    }
    if (textLower.includes('medical') || textLower.includes('vaccination')) {
      issues.push('Medical examination or vaccination documentation needed');
    }
    if (textLower.includes('timeline') || textLower.includes('chronology')) {
      issues.push('Timeline documentation requires clarification');
    }
    if (textLower.includes('insufficient evidence') || textLower.includes('additional evidence')) {
      issues.push('Additional supporting evidence required');
    }

    // Default if no specific issues found
    if (issues.length === 0) {
      issues.push('Specific requirements detailed in uploaded notice - please review carefully');
    }

    return issues;
  };

  const determineNoticeTypeFromFilename = (fileName) => {
    const fileNameLower = fileName.toLowerCase();
    if (fileNameLower.includes('rfe')) return 'RFE';
    if (fileNameLower.includes('noid')) return 'NOID';
    if (fileNameLower.includes('notice')) return 'USCIS Notice';
    return 'Unknown';
  };

  const handleContinue = () => {
    if (uploadedNotice && parsedData.noticeType) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Upload Your USCIS Notice</h3>
        <p className="text-gray-400 text-lg">Let our AI analyze your RFE, NOID, or other USCIS notice</p>
      </div>

      {/* Upload Section */}
      <div className="bg-gray-800 p-8 rounded-lg border-2 border-dashed border-gray-600">
        <div className="text-center">
          {!uploadedNotice ? (
            <>
              <div className="text-6xl mb-4">📄</div>
              <h4 className="text-xl font-semibold text-white mb-2">Upload USCIS Notice</h4>
              <p className="text-gray-400 mb-4">Accepts PDF, JPG, PNG files</p>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
                className="hidden"
                id="notice-upload"
              />
              <label
                htmlFor="notice-upload"
                className="bg-gray-400 text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors cursor-pointer inline-block"
              >
                Choose File
              </label>
            </>
          ) : (
            <div className="text-center">
              <div className="text-4xl mb-2">✅</div>
              <p className="text-white font-medium">{uploadedNotice.name}</p>
              <p className="text-gray-400 text-sm">Uploaded successfully</p>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Progress */}
      {analyzing && (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Notice</h4>
          <p className="text-gray-400">Our AI is extracting key information from your USCIS notice...</p>
        </div>
      )}

      {/* Parsed Results */}
      {parsedData.noticeType && !analyzing && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">📋 Analysis Results</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-300 text-sm mb-1">Notice Type</label>
              <div className="bg-gray-700 p-2 rounded text-white">{parsedData.noticeType}</div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Case Number</label>
              <div className="bg-gray-700 p-2 rounded text-white">{parsedData.caseNumber}</div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Processing Office</label>
              <div className="bg-gray-700 p-2 rounded text-white">{parsedData.processingOffice}</div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Response Deadline</label>
              <div className="bg-red-900/20 border border-red-400 p-2 rounded text-red-300 font-medium">
                ⏰ {parsedData.deadline}
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2">Main Issues Identified</label>
            <div className="space-y-2">
              {parsedData.mainIssues?.map((issue, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-red-400 mr-2">📌</span>
                  <span className="text-gray-300">{issue}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-orange-900/20 border border-orange-400 p-4 rounded">
            <div className="flex items-center mb-2">
              <span className="text-orange-400 mr-2">⚠️</span>
              <span className="text-orange-300 font-medium">Severity Assessment: {parsedData.severity}</span>
            </div>
            <p className="text-orange-200 text-sm">
              This notice requires a comprehensive response with strong evidence to address the identified concerns.
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!uploadedNotice || !parsedData.noticeType || analyzing}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            uploadedNotice && parsedData.noticeType && !analyzing
              ? 'bg-gray-400 text-black hover:bg-gray-300'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Analysis →
        </button>
      </div>
    </div>
  );
};

// RFE Step 2: Smart Breakdown of USCIS Request
const RfeStep2Breakdown = ({ formData, setFormData, onNext, onBack }) => {
  const parsedData = formData.parsedNoticeData || {};
  
  // Helper functions defined first to avoid hoisting issues
  const generateActionItems = (issue, extractedText) => {
    const issueLower = issue.toLowerCase();
    const textLower = extractedText.toLowerCase();
    const actionItems = [];

    // Financial documentation
    if (issueLower.includes('financial') || issueLower.includes('income') || issueLower.includes('support')) {
      actionItems.push('Gather recent tax returns (last 2-3 years)');
      actionItems.push('Collect bank statements showing financial history');
      actionItems.push('Obtain employment verification letters');
      if (textLower.includes('joint') || issueLower.includes('joint')) {
        actionItems.push('Provide joint financial documents (bank accounts, credit cards)');
      }
      if (textLower.includes('affidavit of support') || textLower.includes('i-864')) {
        actionItems.push('Complete or update Form I-864 Affidavit of Support');
      }
    }

    // Relationship evidence
    if (issueLower.includes('relationship') || issueLower.includes('marriage') || issueLower.includes('genuine')) {
      actionItems.push('Compile relationship photos spanning the timeline');
      actionItems.push('Gather communication evidence (emails, texts, calls)');
      actionItems.push('Collect travel documentation for trips together');
      actionItems.push('Obtain affidavits from friends and family');
    }

    // Cohabitation evidence
    if (issueLower.includes('cohabitation') || issueLower.includes('living together') || issueLower.includes('residence')) {
      actionItems.push('Provide joint lease agreements or mortgage documents');
      actionItems.push('Collect utility bills in both names');
      actionItems.push('Gather joint insurance policies');
      actionItems.push('Compile mail addressed to both parties at same address');
    }

    // Identity and documentation
    if (issueLower.includes('identity') || issueLower.includes('birth certificate') || issueLower.includes('passport')) {
      actionItems.push('Provide certified copies of identity documents');
      actionItems.push('Ensure all foreign documents are properly translated');
      actionItems.push('Verify all document authenticity and certification');
    }

    // Medical examination
    if (issueLower.includes('medical') || issueLower.includes('vaccination') || issueLower.includes('health')) {
      actionItems.push('Complete medical examination with USCIS-approved physician');
      actionItems.push('Obtain all required vaccinations');
      actionItems.push('Submit Form I-693 medical examination report');
    }

    // Timeline issues
    if (issueLower.includes('timeline') || issueLower.includes('chronology') || issueLower.includes('dates')) {
      actionItems.push('Review all dates for consistency across documents');
      actionItems.push('Prepare explanatory statement for any discrepancies');
      actionItems.push('Organize evidence in chronological order');
      actionItems.push('Cross-reference dates across all submitted materials');
    }

    // Default action items if no specific category matched
    if (actionItems.length === 0) {
      actionItems.push('Review specific requirements in original notice');
      actionItems.push('Gather all requested documentation');
      actionItems.push('Prepare detailed response addressing this concern');
      actionItems.push('Consider consulting with immigration attorney');
    }

    return actionItems;
  };

  const generateIssueDescription = (issue, extractedText) => {
    const issueLower = issue.toLowerCase();
    
    if (issueLower.includes('financial')) {
      return 'USCIS requires additional financial evidence to establish ability to support the beneficiary.';
    } else if (issueLower.includes('relationship')) {
      return 'USCIS needs more evidence to establish the authenticity of your relationship.';
    } else if (issueLower.includes('cohabitation')) {
      return 'USCIS requires evidence that you and your spouse live together in a marital relationship.';
    } else if (issueLower.includes('timeline')) {
      return 'USCIS has identified inconsistencies in the timeline of events that need clarification.';
    } else if (issueLower.includes('medical')) {
      return 'USCIS requires medical examination documentation to proceed with your case.';
    } else if (issueLower.includes('identity')) {
      return 'USCIS needs additional identity documentation or verification.';
    }
    
    return `USCIS has raised concerns about: ${issue}. This requires immediate attention and comprehensive response.`;
  };

  const assessRiskLevel = (issue, noticeType) => {
    const issueLower = issue.toLowerCase();
    
    // NOID notices are always high risk
    if (noticeType === 'NOID') return 'High';
    
    // Critical issues
    if (issueLower.includes('insufficient') || issueLower.includes('failure to establish') || 
        issueLower.includes('does not establish') || issueLower.includes('fraud')) {
      return 'High';
    }
    
    // Important but addressable issues
    if (issueLower.includes('additional') || issueLower.includes('more evidence') || 
        issueLower.includes('clarification')) {
      return 'Medium';
    }
    
    return 'Medium'; // Default for RFE items
  };

  const determineTimeline = (issue, extractedText) => {
    const issueLower = issue.toLowerCase();
    const textLower = extractedText.toLowerCase();
    
    if (issueLower.includes('financial') || issueLower.includes('tax')) {
      return 'Must be recent - typically within last 12 months';
    } else if (issueLower.includes('relationship') || issueLower.includes('cohabitation')) {
      return 'Should span the entire relationship period';
    } else if (issueLower.includes('medical')) {
      return 'Medical exam must be completed by USCIS-approved physician';
    } else if (textLower.includes('immediate') || issueLower.includes('urgent')) {
      return 'Requires immediate attention';
    }
    
    return 'Address as soon as possible';
  };

  const generateContextualInsights = (parsedData) => {
    const insights = [];
    const noticeType = parsedData.noticeType;
    const severity = parsedData.severity;
    const deadline = parsedData.deadline;
    
    // Notice type specific insights
    if (noticeType === 'RFE') {
      insights.push('RFE responses typically have 30-87 days for response - verify your specific deadline');
      insights.push('Thorough response addressing all points can lead to case approval');
    } else if (noticeType === 'NOID') {
      insights.push('NOID indicates USCIS intends to deny - this is your final opportunity to provide evidence');
      insights.push('Consider immediate legal consultation given the high stakes of NOID responses');
    }
    
    // Severity specific insights
    if (severity === 'High') {
      insights.push('High severity indicates multiple or critical deficiencies requiring comprehensive response');
    } else if (severity === 'Low') {
      insights.push('Lower severity suggests focused issues that can be addressed with targeted evidence');
    }
    
    // Timeline insights
    if (deadline && deadline !== 'Deadline not found - check original notice') {
      insights.push(`Your response deadline is ${deadline} - plan submission well in advance`);
    }
    
    // General insights
    insights.push('Quality of evidence matters more than quantity - focus on relevance and authenticity');
    insights.push('Clear organization and professional presentation can positively impact your case');
    
    return insights;
  };
  
  // Generate dynamic breakdown based on parsed notice data
  const generateDynamicBreakdown = () => {
    const mainIssues = parsedData.mainIssues || [];
    const extractedText = parsedData.extractedText || '';
    const noticeType = parsedData.noticeType || 'Unknown';
    
    const dynamicConcerns = mainIssues.map((issue, index) => {
      // Generate specific action items based on the issue type
      const actionItems = generateActionItems(issue, extractedText);
      const riskLevel = assessRiskLevel(issue, noticeType);
      const timeline = determineTimeline(issue, extractedText);
      
      return {
        issue: issue,
        description: generateIssueDescription(issue, extractedText),
        actionItems: actionItems,
        riskLevel: riskLevel,
        timeline: timeline,
        sourceAnalysis: `Extracted from ${parsedData.fileName || 'uploaded notice'}`
      };
    });

    // If no issues were extracted, create fallback based on notice type
    if (dynamicConcerns.length === 0) {
      dynamicConcerns.push({
        issue: `${noticeType} requires comprehensive response`,
        description: `Your ${noticeType} notice requires careful review and response to all specified requirements.`,
        actionItems: [
          'Carefully review the original notice for all specific requirements',
          'Gather all requested documentation',
          'Prepare detailed response addressing each point',
          'Consider legal consultation for complex issues'
        ],
        riskLevel: noticeType === 'NOID' ? 'High' : 'Medium',
        timeline: parsedData.deadline || 'Check original notice for deadline',
        sourceAnalysis: 'Generated from notice analysis'
      });
    }

    return dynamicConcerns;
  };

  const detailedBreakdown = {
    mainConcerns: generateDynamicBreakdown(),
    insights: generateContextualInsights(parsedData),
    analysisMetadata: {
      sourceFile: parsedData.fileName,
      analysisDate: parsedData.analysisDate,
      noticeType: parsedData.noticeType,
      extractionMethod: parsedData.parsingError ? 'Filename analysis' : 'Text analysis'
    }
  };

  const handleContinue = () => {
    setFormData({
      ...formData,
      detailedBreakdown
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">USCIS Request Breakdown</h3>
        <p className="text-gray-400 text-lg">Understanding what USCIS is really asking for</p>
      </div>

      {/* Main Concerns */}
      <div className="space-y-4">
        {detailedBreakdown.mainConcerns.map((concern, index) => (
          <div key={index} className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-white mb-2">
                  📌 {concern.issue}
                </h4>
                <p className="text-gray-300 mb-3">{concern.description}</p>
              </div>
              <div className={`px-3 py-1 rounded text-sm font-medium ${
                concern.riskLevel === 'High' ? 'bg-red-900/20 text-red-300 border border-red-400' :
                concern.riskLevel === 'Medium' ? 'bg-yellow-900/20 text-yellow-300 border border-yellow-400' :
                'bg-blue-900/20 text-blue-300 border border-blue-400'
              }`}>
                {concern.riskLevel} Risk
              </div>
            </div>

            <div className="mb-4">
              <h5 className="text-white font-medium mb-2">Action Items:</h5>
              <ul className="space-y-1">
                {concern.actionItems.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start text-gray-300">
                    <span className="text-emerald-400 mr-2">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="text-sm text-gray-400">
              <strong>Timeline:</strong> {concern.timeline}
            </div>
          </div>
        ))}
      </div>

      {/* Case Insights */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-emerald-300 mb-4">💡 Similar Case Insights</h4>
        <ul className="space-y-2">
          {detailedBreakdown.insights.map((insight, index) => (
            <li key={index} className="flex items-start text-emerald-200">
              <span className="text-emerald-400 mr-2">✓</span>
              {insight}
            </li>
          ))}
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Upload
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to Documents →
        </button>
      </div>
    </div>
  );
};

// RFE Step 3: Document Upload Portal
const RfeStep3DocumentUpload = ({ formData, setFormData, onNext, onBack }) => {
  const [uploadedDocs, setUploadedDocs] = useState(formData.documents || []);
  const [uploadCategory, setUploadCategory] = useState('new-evidence');

  const uploadCategories = [
    {
      id: 'notice',
      title: 'USCIS Notice',
      description: 'Upload USCIS notice (if not yet uploaded)',
      icon: '📋'
    },
    {
      id: 'previous-evidence',
      title: 'Previously Submitted Evidence',
      description: 'Upload previously submitted evidence (if applicable)',
      icon: '📄'
    },
    {
      id: 'new-evidence',
      title: 'New Documents',
      description: 'Upload new documents responding to the request',
      icon: '📎'
    }
  ];

  const handleFileUpload = async (category, file) => {
    try {
      // Convert file to base64 for OCR analysis
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);

      // Call backend for actual document analysis with Google Vision OCR
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          document_file: fileData,
          document_name: file.name,
          document_type: category,
          case_context: {
            case_type: 'RFE/NOID Response',
            agent_type: 'rfe-noid',
            case_number: formData.parsedData?.caseNumber || 'Unknown',
            notice_type: formData.parsedData?.noticeType || 'RFE/NOID'
          }
        })
      });

      let documentAnalysis;
      if (response.ok) {
        documentAnalysis = await response.json();
        console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
      } else {
        const errorText = await response.text();
        console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
        documentAnalysis = {
          document_type: category,
          content_description: `Document analysis failed: ${errorText}`,
          case_relevance: "Manual review required to determine relevance",
          exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
          immigration_value: "Medium",
          analysis_error: `Analysis failed: ${errorText}`
        };
      }

      // Create document object with OCR analysis results
      const newDoc = {
        id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        category: category,
        uploaded: true,
        timestamp: new Date().toISOString(),
        // Add OCR analysis results
        analysis: documentAnalysis,
        content_description: documentAnalysis.content_description,
        parties_mentioned: documentAnalysis.parties_mentioned,
        key_dates: documentAnalysis.key_dates,
        case_relevance: documentAnalysis.case_relevance,
        exhibit_description: documentAnalysis.exhibit_description,
        immigration_value: documentAnalysis.immigration_value,
        red_flags: documentAnalysis.red_flags,
        authenticity_indicators: documentAnalysis.authenticity_indicators,
        extracted_text: documentAnalysis.extracted_text
      };
      
      const updatedDocs = [...uploadedDocs, newDoc];
      setUploadedDocs(updatedDocs);
      
    } catch (error) {
      console.error('Document analysis error:', error);
      
      // Add document with error indication
      const newDoc = {
        id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        category: category,
        uploaded: true,
        timestamp: new Date().toISOString(),
        analysis_error: "Analysis failed - manual review required",
        content_description: `Document analysis failed for ${file.name}`,
        case_relevance: "Manual review required",
        exhibit_description: `Exhibit: ${file.name} (manual review required)`,
        immigration_value: "Unknown"
      };
      
      const updatedDocs = [...uploadedDocs, newDoc];
      setUploadedDocs(updatedDocs);
    }
  };

  const handleContinue = () => {
    setFormData({ ...formData, documents: uploadedDocs });
    onNext();
  };

  const removeDocument = (docId) => {
    setUploadedDocs(uploadedDocs.filter(doc => doc.id !== docId));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Document Upload Portal</h3>
        <p className="text-gray-400 text-lg">Upload all relevant documents for your response</p>
      </div>

      {/* Upload Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {uploadCategories.map((category) => (
          <div
            key={category.id}
            onClick={() => setUploadCategory(category.id)}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              uploadCategory === category.id
                ? 'border-gray-400 bg-gray-800'
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
          >
            <div className="text-center">
              <div className="text-2xl mb-2">{category.icon}</div>
              <h4 className="text-white font-medium mb-1">{category.title}</h4>
              <p className="text-gray-400 text-sm">{category.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* File Upload Area */}
      <div className="bg-gray-800 p-6 rounded-lg border-2 border-dashed border-gray-600">
        <div className="text-center">
          <div className="text-4xl mb-4">📁</div>
          <h4 className="text-xl font-semibold text-white mb-2">
            Upload {uploadCategories.find(cat => cat.id === uploadCategory)?.title}
          </h4>
          <p className="text-gray-400 mb-4">Drag & drop files here or click to browse</p>
          <input
            type="file"
            multiple
            onChange={(e) => {
              Array.from(e.target.files).forEach(file => {
                handleFileUpload(uploadCategory, file);
              });
            }}
            className="hidden"
            id="doc-upload"
          />
          <label
            htmlFor="doc-upload"
            className="bg-gray-400 text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors cursor-pointer inline-block"
          >
            Choose Files
          </label>
        </div>
      </div>

      {/* Uploaded Documents */}
      {uploadedDocs.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">Uploaded Documents ({uploadedDocs.length})</h4>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {uploadedDocs.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between bg-gray-700 p-3 rounded">
                <div className="flex items-center">
                  <span className="text-gray-400 mr-3">📄</span>
                  <div>
                    <div className="text-white text-sm">{doc.name}</div>
                    <div className="text-gray-400 text-xs">
                      {uploadCategories.find(cat => cat.id === doc.category)?.title} • 
                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeDocument(doc.id)}
                  className="text-red-400 hover:text-red-300 text-sm px-2 py-1"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Guidelines */}
      <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
        <h5 className="text-blue-300 font-medium mb-2">📋 Upload Guidelines</h5>
        <ul className="text-blue-200 text-sm space-y-1">
          <li>• Ensure all documents are clear and readable</li>
          <li>• Include English translations for foreign language documents</li>
          <li>• Organize documents by relevance to USCIS requests</li>
          <li>• Maximum file size: 10MB per document</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Breakdown
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to Strategy →
        </button>
      </div>
    </div>
  );
};

// RFE Step 4: Evidence Strategy Session
const RfeStep4EvidenceStrategy = ({ formData, setFormData, onNext, onBack }) => {
  const [strategyAnswers, setStrategyAnswers] = useState(formData.evidenceStrategy || {});

  const strategicQuestions = [
    {
      id: 'available_evidence',
      question: 'What evidence do you have access to but haven\'t uploaded yet?',
      type: 'textarea',
      placeholder: 'List any documents, records, or evidence you can obtain...'
    },
    {
      id: 'timeline_constraints',
      question: 'Can you obtain specific documents before the deadline?',
      type: 'multiselect',
      options: [
        'Joint bank statements (past 12 months)',
        'Tax returns (past 2 years)',
        'Lease agreements or mortgage documents',
        'Utility bills in both names',
        'Insurance policies',
        'Medical records',
        'Employment verification letters',
        'Other financial documents'
      ]
    },
    {
      id: 'affidavit_sources',
      question: 'Who could write a strong affidavit for your case?',
      type: 'checklist',
      options: [
        'Family members who know the relationship',
        'Friends who have witnessed the relationship',
        'Neighbors or community members',
        'Employers or colleagues',
        'Religious or community leaders',
        'Healthcare providers',
        'Financial advisors or accountants'
      ]
    },
    {
      id: 'special_circumstances',
      question: 'Are there any special circumstances that need explanation?',
      type: 'textarea',
      placeholder: 'Describe any unique situations, gaps in documentation, or special circumstances...'
    }
  ];

  const handleAnswerChange = (questionId, value) => {
    setStrategyAnswers({
      ...strategyAnswers,
      [questionId]: value
    });
  };

  const generateEvidenceChecklist = () => {
    const checklist = [];
    
    // Based on the main concerns from previous step
    const concerns = formData.detailedBreakdown?.mainConcerns || [];
    
    concerns.forEach(concern => {
      concern.actionItems.forEach(item => {
        checklist.push({
          category: concern.issue,
          item: item,
          priority: concern.riskLevel === 'High' ? 'Priority 1: Must-have' : 
                   concern.riskLevel === 'Medium' ? 'Priority 2: Supplementary' : 
                   'Priority 3: Optional',
          available: strategyAnswers.timeline_constraints?.includes(item) || false
        });
      });
    });

    return checklist;
  };

  const handleContinue = () => {
    const evidenceChecklist = generateEvidenceChecklist();
    setFormData({
      ...formData,
      evidenceStrategy: strategyAnswers,
      evidenceChecklist
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Evidence Strategy Session</h3>
        <p className="text-gray-400 text-lg">Let's plan your response strategy</p>
      </div>

      {/* Strategic Questions */}
      <div className="space-y-6">
        {strategicQuestions.map((question) => (
          <div key={question.id} className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-3">{question.question}</h4>
            
            {question.type === 'textarea' && (
              <textarea
                value={strategyAnswers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                placeholder={question.placeholder}
                rows={4}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-gray-400"
              />
            )}

            {question.type === 'multiselect' && (
              <div className="space-y-2">
                {question.options.map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={(strategyAnswers[question.id] || []).includes(option)}
                      onChange={(e) => {
                        const current = strategyAnswers[question.id] || [];
                        const updated = e.target.checked
                          ? [...current, option]
                          : current.filter(item => item !== option);
                        handleAnswerChange(question.id, updated);
                      }}
                      className="mr-3 w-4 h-4"
                    />
                    <span className="text-gray-300">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {question.type === 'checklist' && (
              <div className="space-y-2">
                {question.options.map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={(strategyAnswers[question.id] || []).includes(option)}
                      onChange={(e) => {
                        const current = strategyAnswers[question.id] || [];
                        const updated = e.target.checked
                          ? [...current, option]
                          : current.filter(item => item !== option);
                        handleAnswerChange(question.id, updated);
                      }}
                      className="mr-3 w-4 h-4"
                    />
                    <span className="text-gray-300">{option}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Generated Evidence Checklist Preview */}
      {Object.keys(strategyAnswers).length > 0 && (
        <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
          <h4 className="text-emerald-300 font-semibold mb-3">📋 Your Evidence Action Plan</h4>
          <div className="space-y-2">
            {generateEvidenceChecklist().slice(0, 5).map((item, index) => (
              <div key={index} className="flex items-start">
                <span className={`mr-2 ${
                  item.priority.includes('Priority 1') ? 'text-red-400' :
                  item.priority.includes('Priority 2') ? 'text-yellow-400' :
                  'text-blue-400'
                }`}>
                  {item.priority.includes('Priority 1') ? '🔴' :
                   item.priority.includes('Priority 2') ? '🟡' : '🔵'}
                </span>
                <span className="text-emerald-200 text-sm">{item.item}</span>
              </div>
            ))}
            {generateEvidenceChecklist().length > 5 && (
              <p className="text-emerald-300 text-sm">...and {generateEvidenceChecklist().length - 5} more items</p>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Documents
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to Analysis →
        </button>
      </div>
    </div>
  );
};
// RFE Step 5: AI Document Analyzer
const RfeStep5DocumentAnalyzer = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.analysisResults || {});

  const documents = formData.documents || [];

  const handleAnalyze = () => {
    setAnalyzing(true);
    
    setTimeout(() => {
      // Enhanced dynamic document matching analysis
      const uscisRequests = formData.parsedNoticeData?.requests || formData.detailedBreakdown?.mainConcerns || [];
      const uploadedDocs = formData.documents || documents.filter(doc => doc.category === 'new-evidence');
      
      const documentMatching = {
        addressedRequests: 0,
        partiallyAddressed: 0,
        unaddressed: 0,
        detailedMatching: []
      };

      // Analyze each USCIS request with sophisticated matching
      uscisRequests.forEach((request, index) => {
        const requestKeywords = [
          ...(request.actionItems || []),
          (request.issue || '').toLowerCase().split(' ').filter(word => word.length > 3)
        ].flat().map(item => item.toLowerCase());
        
        const matchingDocs = uploadedDocs.filter(doc => {
          const docName = doc.name.toLowerCase();
          const docCategory = doc.category?.toLowerCase() || '';
          
          // Enhanced matching algorithm
          return requestKeywords.some(keyword => 
            docName.includes(keyword) || 
            docCategory.includes(keyword) ||
            // Semantic matching for common document types
            (keyword.includes('financial') && (docName.includes('bank') || docName.includes('tax') || docCategory.includes('financial'))) ||
            (keyword.includes('relationship') && (docName.includes('photo') || docName.includes('communication') || docCategory.includes('personal'))) ||
            (keyword.includes('identity') && (docName.includes('passport') || docName.includes('birth') || docCategory.includes('identity')))
          );
        });

        // Dynamic scoring based on request complexity and evidence quality
        let status = 'Not Addressed';
        let score = 0;
        const requiredItems = (request.actionItems || []).length || 1;
        const providedItems = matchingDocs.length;
        
        // Calculate score based on coverage ratio and document quality
        const coverageRatio = providedItems / Math.max(requiredItems, 1);
        const baseScore = Math.min(100, coverageRatio * 100);
        
        // Bonus for document quality factors
        let qualityBonus = 0;
        matchingDocs.forEach(doc => {
          if (doc.date && new Date(doc.date) > new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)) {
            qualityBonus += 5; // Recent documents
          }
          if (doc.category === 'government-forms') {
            qualityBonus += 10; // Official documents
          }
          if (doc.size && doc.size > 100000) {
            qualityBonus += 3; // Substantial documents
          }
        });
        
        score = Math.min(100, baseScore + qualityBonus);
        
        // Dynamic status assignment
        if (score >= 90) {
          status = 'Excellently Addressed';
          documentMatching.addressedRequests++;
        } else if (score >= 70) {
          status = 'Well Addressed';
          documentMatching.addressedRequests++;
        } else if (score >= 40) {
          status = 'Partially Addressed';
          documentMatching.partiallyAddressed++;
        } else {
          status = 'Inadequately Addressed';
          documentMatching.unaddressed++;
        }

        // Generate specific recommendations based on actual gaps
        const recommendations = [];
        if (score < 70) {
          if (requiredItems > providedItems) {
            const missingItems = requiredItems - providedItems;
            recommendations.push(`Upload ${missingItems} additional document(s) for: ${request.issue || 'this requirement'}`);
          }
          if ((request.actionItems || []).some(item => item.toLowerCase().includes('certified'))) {
            recommendations.push('Ensure documents are certified copies if required by USCIS');
          }
          if (!matchingDocs.some(doc => doc.category === 'government-forms')) {
            recommendations.push('Include official government documents if available');
          }
        }

        documentMatching.detailedMatching.push({
          request: request.issue || `Requirement ${index + 1}`,
          requiredItems: requiredItems,
          providedItems: providedItems,
          matchingDocuments: matchingDocs.map(doc => doc.name),
          status: status,
          score: Math.round(score),
          coverage: Math.round(coverageRatio * 100),
          qualityBonus: qualityBonus,
          recommendations: recommendations
        });
      });

      // Calculate sophisticated overall matching score
      const totalScore = documentMatching.detailedMatching.reduce((sum, item) => sum + item.score, 0);
      const avgScore = uscisRequests.length > 0 ? totalScore / uscisRequests.length : 0;
      
      // Dynamic scoring thresholds based on case complexity
      const caseComplexity = uscisRequests.length;
      const adjustedThresholds = {
        strong: Math.max(75, 85 - (caseComplexity * 2)), // Harder for complex cases
        moderate: Math.max(50, 65 - (caseComplexity * 2))
      };
      
      if (avgScore >= adjustedThresholds.strong) {
        documentMatching.overallScore = 'Strong';
        documentMatching.scoreCategory = 'excellent';
      } else if (avgScore >= adjustedThresholds.moderate) {
        documentMatching.overallScore = 'Moderate';
        documentMatching.scoreCategory = 'good';
      } else {
        documentMatching.overallScore = 'Needs Improvement';
        documentMatching.scoreCategory = 'weak';
      }
      
      documentMatching.averageScore = Math.round(avgScore);
      documentMatching.totalDocuments = uploadedDocs.length;
      documentMatching.caseComplexity = caseComplexity;

      // Enhanced dynamic consistency analysis based on actual documents
      const consistencyCheck = {
        namesConsistent: true,
        datesConsistent: true,
        addressesConsistent: true,
        issues: []
      };

      // Analyze document consistency patterns
      const names = [];
      const dates = [];
      const addresses = [];
      
      uploadedDocs.forEach(doc => {
        // Extract names from document names (simplified analysis)
        const nameMatches = doc.name.match(/[A-Z][a-z]+ [A-Z][a-z]+/g);
        if (nameMatches) names.push(...nameMatches);
        
        // Extract dates
        if (doc.date) dates.push(doc.date);
        
        // Extract addresses if present in document metadata or names
        const addressPattern = /\d+.*(?:st|street|ave|avenue|rd|road|blvd|boulevard)/i;
        if (addressPattern.test(doc.name)) {
          addresses.push(doc.name.match(addressPattern)[0]);
        }
      });

      // Check name consistency
      if (names.length > 1) {
        const uniqueNames = [...new Set(names.map(name => name.toLowerCase()))];
        if (uniqueNames.length > 2) { // Allow for reasonable variations
          consistencyCheck.namesConsistent = false;
          consistencyCheck.issues.push(`Name variations found: ${uniqueNames.slice(0, 3).join(', ')}`);
        }
      }

      // Check date consistency
      if (dates.length > 1) {
        const sortedDates = dates.sort();
        const dateRange = new Date(sortedDates[sortedDates.length - 1]) - new Date(sortedDates[0]);
        const yearsSpan = dateRange / (1000 * 60 * 60 * 24 * 365);
        
        if (yearsSpan > 10) { // Unusual date span
          consistencyCheck.datesConsistent = false;
          consistencyCheck.issues.push(`Wide date range (${Math.round(yearsSpan)} years) - verify timeline accuracy`);
        }
      }

      // Check address consistency
      if (addresses.length > 1) {
        const uniqueAddresses = [...new Set(addresses.map(addr => addr.toLowerCase()))];
        if (uniqueAddresses.length > 3) { // Too many address variations
          consistencyCheck.addressesConsistent = false;
          consistencyCheck.issues.push('Multiple address variations found - verify current residence');
        }
      }

      // Enhanced temporal coverage analysis
      const temporalCoverage = {
        coveragePercentage: 0,
        gaps: [],
        recommendation: ''
      };

      if (dates.length > 0) {
        const sortedDates = dates.sort();
        const earliestDate = new Date(sortedDates[0]);
        const latestDate = new Date(sortedDates[sortedDates.length - 1]);
        const totalTimespan = latestDate - earliestDate;
        
        // Calculate coverage based on document distribution
        if (totalTimespan > 0) {
          const monthsSpanned = totalTimespan / (1000 * 60 * 60 * 24 * 30);
          const docsPerMonth = dates.length / monthsSpanned;
          temporalCoverage.coveragePercentage = Math.min(100, Math.round(docsPerMonth * 30));
          
          if (temporalCoverage.coveragePercentage < 60) {
            temporalCoverage.gaps.push('Insufficient document coverage over time period');
            temporalCoverage.recommendation = 'Add more recent documents to improve timeline coverage';
          } else if (temporalCoverage.coveragePercentage < 80) {
            temporalCoverage.recommendation = 'Good temporal coverage - consider filling any obvious gaps';
          } else {
            temporalCoverage.recommendation = 'Excellent temporal coverage of relationship/case timeline';
          }
        }
      } else {
        temporalCoverage.coveragePercentage = 0;
        temporalCoverage.gaps.push('No dated documents provided');
        temporalCoverage.recommendation = 'Add documents with clear dates to establish timeline';
      }

      // Enhanced document quality analysis based on actual documents
      const documentQuality = uploadedDocs.map(doc => {
        let rating = 'Moderate';
        const issues = [];
        const strengths = [];
        
        // Analyze by file size (rough quality indicator)
        if (doc.size) {
          if (doc.size > 500000) {
            strengths.push('High resolution/detailed document');
            rating = 'Strong';
          } else if (doc.size < 50000) {
            issues.push('Small file size - may need higher resolution scan');
            rating = 'Weak';
          }
        }
        
        // Analyze by category
        if (doc.category === 'government-forms') {
          strengths.push('Official government document');
          rating = 'Strong';
        } else if (doc.category === 'personal-documents') {
          strengths.push('Personal evidence provided');
        }
        
        // Analyze by name patterns
        if (doc.name.toLowerCase().includes('certified')) {
          strengths.push('Certified document');
          rating = 'Strong';
        }
        if (doc.name.toLowerCase().includes('copy')) {
          issues.push('Verify if certified copy is required');
        }
        
        // Recent document bonus
        if (doc.date && new Date(doc.date) > new Date(Date.now() - 180 * 24 * 60 * 60 * 1000)) {
          strengths.push('Recent document (within 6 months)');
        }
        
        return {
          name: doc.name,
          rating: rating,
          issues: issues,
          strengths: strengths.length > 0 ? strengths : ['Document uploaded successfully']
        };
      });

      const dynamicAnalysis = {
        documentMatching,
        consistencyCheck,
        temporalCoverage,
        documentQuality
      };
      
      setAnalysisResults(dynamicAnalysis);
      setFormData({ ...formData, analysisResults: dynamicAnalysis });
      setAnalyzing(false);
    }, 3000);
  };

  React.useEffect(() => {
    if (documents.length > 0 && Object.keys(analysisResults).length === 0) {
      handleAnalyze();
    }
  }, []);

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Strong': return 'text-emerald-400';
      case 'Weak': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const getRatingIcon = (rating) => {
    switch (rating) {
      case 'Strong': return '✅';
      case 'Weak': return '❌';
      default: return '⚠️';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">AI Document Analyzer</h3>
        <p className="text-gray-400 text-lg">Comprehensive analysis of your uploaded evidence</p>
      </div>

      {analyzing && (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Documents</h4>
          <p className="text-gray-400">Our AI is matching documents to USCIS requests...</p>
        </div>
      )}

      {analysisResults.documentMatching && !analyzing && (
        <>
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📋 Document Matching Analysis</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400 mb-1">
                  {analysisResults.documentMatching.addressedRequests}
                </div>
                <div className="text-gray-400 text-sm">Fully Addressed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400 mb-1">
                  {analysisResults.documentMatching.partiallyAddressed}
                </div>
                <div className="text-gray-400 text-sm">Partially Addressed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-400 mb-1">
                  {analysisResults.documentMatching.unaddressed}
                </div>
                <div className="text-gray-400 text-sm">Not Addressed</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold mb-1 ${getRatingColor(analysisResults.documentMatching.overallScore)}`}>
                  {analysisResults.documentMatching.overallScore}
                </div>
                <div className="text-gray-400 text-sm">Overall Score</div>
              </div>
            </div>

            {/* Detailed Matching Breakdown */}
            <div className="space-y-4">
              <h5 className="text-lg font-medium text-white">Detailed Request Analysis:</h5>
              {analysisResults.documentMatching.detailedMatching?.map((match, index) => (
                <div key={index} className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h6 className="text-white font-medium">{match.request}</h6>
                      <p className="text-gray-300 text-sm">
                        {match.providedItems}/{match.requiredItems} items provided
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded text-sm font-medium ${
                      match.status === 'Fully Addressed' ? 'bg-emerald-900/20 text-emerald-300 border border-emerald-400' :
                      match.status === 'Partially Addressed' ? 'bg-yellow-900/20 text-yellow-300 border border-yellow-400' :
                      'bg-red-900/20 text-red-300 border border-red-400'
                    }`}>
                      {match.status}
                    </div>
                  </div>
                  
                  {match.matchingDocuments.length > 0 && (
                    <div className="mb-3">
                      <p className="text-gray-400 text-xs mb-1">Matching Documents:</p>
                      <div className="flex flex-wrap gap-2">
                        {match.matchingDocuments.map((docName, docIndex) => (
                          <span key={docIndex} className="text-xs bg-gray-600 text-gray-200 px-2 py-1 rounded">
                            {docName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {match.recommendations.length > 0 && (
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Recommendations:</p>
                      <ul className="space-y-1">
                        {match.recommendations.map((rec, recIndex) => (
                          <li key={recIndex} className="text-yellow-300 text-xs flex items-start">
                            <span className="text-yellow-400 mr-1">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">🔍 Consistency Analysis</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Names Consistency</span>
                <span className={analysisResults.consistencyCheck.namesConsistent ? 'text-emerald-400' : 'text-red-400'}>
                  {analysisResults.consistencyCheck.namesConsistent ? '✅ Consistent' : '❌ Issues Found'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Dates Consistency</span>
                <span className={analysisResults.consistencyCheck.datesConsistent ? 'text-emerald-400' : 'text-red-400'}>
                  {analysisResults.consistencyCheck.datesConsistent ? '✅ Consistent' : '❌ Issues Found'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Addresses Consistency</span>
                <span className={analysisResults.consistencyCheck.addressesConsistent ? 'text-emerald-400' : 'text-red-400'}>
                  {analysisResults.consistencyCheck.addressesConsistent ? '✅ Consistent' : '❌ Issues Found'}
                </span>
              </div>
            </div>
            
            {analysisResults.consistencyCheck.issues.length > 0 && (
              <div className="mt-4 p-4 bg-red-900/20 border border-red-400 rounded">
                <h5 className="text-red-300 font-medium mb-2">Issues Found:</h5>
                <ul className="space-y-1">
                  {analysisResults.consistencyCheck.issues.map((issue, index) => (
                    <li key={index} className="text-red-200 text-sm flex items-start">
                      <span className="text-red-400 mr-2">•</span>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📄 Document Quality</h4>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {analysisResults.documentQuality.map((doc, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700 p-3 rounded">
                  <div className="flex items-center">
                    <span className="text-gray-400 mr-3">📄</span>
                    <div>
                      <div className="text-white text-sm">{doc.name}</div>
                      {doc.issues.length > 0 && (
                        <div className="text-red-300 text-xs">Issues: {doc.issues.join(', ')}</div>
                      )}
                    </div>
                  </div>
                  <div className={`text-sm font-medium ${getRatingColor(doc.rating)}`}>
                    {getRatingIcon(doc.rating)} {doc.rating}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Strategy
        </button>
        <button
          onClick={() => {
            if (!analyzing && analysisResults.documentMatching) {
              onNext();
            }
          }}
          disabled={analyzing || !analysisResults.documentMatching}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !analyzing && analysisResults.documentMatching
              ? 'bg-gray-400 text-black hover:bg-gray-300'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Gap Analysis →
        </button>
      </div>
    </div>
  );
};

// RFE Step 6: Evidence Gap Analysis & Prioritization
const RfeStep6GapAnalysis = ({ formData, setFormData, onNext, onBack }) => {
  const [gapAnalysis, setGapAnalysis] = useState(formData.gapAnalysis || {});

  React.useEffect(() => {
    if (Object.keys(gapAnalysis).length === 0) {
      const gaps = [
        {
          category: 'Missing High-Priority Evidence',
          impact: 'High',
          description: 'Directly addresses USCIS request',
          items: [
            'Joint tax returns for 2022-2023',
            'Utility bills in both names for past 6 months',
            'Joint bank statements showing regular activity'
          ]
        },
        {
          category: 'Weak Supporting Evidence',
          impact: 'Medium', 
          description: 'Strengthens related areas',
          items: [
            'Additional photos from family events',
            'Employment verification letter mentioning spouse'
          ]
        }
      ];

      const prioritization = {
        'Priority 1: Must-have': 3,
        'Priority 2: Supplementary': 2,
        'Priority 3: Optional': 1
      };

      const sufficiencyOutputs = [
        'Need 3+ joint financial records for complete coverage',
        'Evidence covers substantial portion of relationship timeline'
      ];

      const newGapAnalysis = { gaps, prioritization, sufficiencyOutputs };
      setGapAnalysis(newGapAnalysis);
      setFormData({ ...formData, gapAnalysis: newGapAnalysis });
    }
  }, []);

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High': return 'bg-red-900/20 text-red-300 border-red-400';
      case 'Medium': return 'bg-yellow-900/20 text-yellow-300 border-yellow-400';
      case 'Low': return 'bg-blue-900/20 text-blue-300 border-blue-400';
      default: return 'bg-gray-900/20 text-gray-300 border-gray-400';
    }
  };

  const getImpactIcon = (impact) => {
    switch (impact) {
      case 'High': return '🔴';
      case 'Medium': return '🟡';
      case 'Low': return '🔵';
      default: return '⚪';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Evidence Gap Analysis</h3>
        <p className="text-gray-400 text-lg">Identify and prioritize missing evidence</p>
      </div>

      <div className="space-y-4">
        {gapAnalysis.gaps?.map((gap, index) => (
          <div key={index} className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <span className="mr-2">{getImpactIcon(gap.impact)}</span>
                  <h4 className="text-lg font-semibold text-white">{gap.category}</h4>
                </div>
                <p className="text-gray-300 text-sm">{gap.description}</p>
              </div>
              <div className={`px-3 py-1 rounded text-sm font-medium border ${getImpactColor(gap.impact)}`}>
                {gap.impact} Impact
              </div>
            </div>

            <div className="space-y-2">
              {gap.items.map((item, itemIndex) => (
                <div key={itemIndex} className="flex items-start bg-gray-700 p-3 rounded">
                  <span className="text-gray-400 mr-3">📋</span>
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📊 Priority Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(gapAnalysis.prioritization || {}).map(([priority, count]) => (
            <div key={priority} className="text-center">
              <div className={`text-2xl font-bold mb-1 ${
                priority.includes('Priority 1') ? 'text-red-400' :
                priority.includes('Priority 2') ? 'text-yellow-400' :
                'text-blue-400'
              }`}>
                {count}
              </div>
              <div className="text-gray-400 text-sm">{priority}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Analysis
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to Additional Documents →
        </button>
      </div>
    </div>
  );
};

// RFE Step 7: Additional Document Upload
function RfeStep7AdditionalDocuments({ formData, setFormData, onNext, onBack }) {
  const [newDocuments, setNewDocuments] = useState(formData.newDocuments || []);
  const [uploadCategory, setUploadCategory] = useState('gap-addressing');

  const gapAnalysis = formData.gapAnalysis || {};
  const highPriorityGaps = gapAnalysis.gaps?.filter(gap => gap.impact === 'High') || [];
  const mediumPriorityGaps = gapAnalysis.gaps?.filter(gap => gap.impact === 'Medium') || [];

  const uploadCategories = [
    {
      id: 'gap-addressing',
      title: 'Gap-Addressing Documents',
      description: 'Documents that directly address identified gaps',
      icon: '🎯',
      priority: 'high'
    },
    {
      id: 'supporting',
      title: 'Additional Supporting Evidence',
      description: 'Extra documents to strengthen your case',
      icon: '📄',
      priority: 'medium'
    },
    {
      id: 'clarification',
      title: 'Clarification Documents',
      description: 'Documents that clarify timeline or circumstances',
      icon: '📋',
      priority: 'low'
    }
  ];

  const handleFileUpload = async (category, file) => {
    try {
      // Convert file to base64 for OCR analysis
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      console.log(`🔍 Analyzing document: ${file.name} (${file.type}, ${file.size} bytes)`);

      // Call backend for actual document analysis with Google Vision OCR
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          document_file: fileData,
          document_name: file.name,
          document_type: category,
          case_context: {
            case_type: 'RFE/NOID Response - Additional Evidence',
            agent_type: 'rfe-noid',
            case_number: formData.parsedData?.caseNumber || 'Unknown',
            notice_type: formData.parsedData?.noticeType || 'RFE/NOID'
          }
        })
      });

      let documentAnalysis;
      if (response.ok) {
        documentAnalysis = await response.json();
        console.log(`✅ Document analysis successful for ${file.name}:`, documentAnalysis);
      } else {
        const errorText = await response.text();
        console.error(`❌ Document analysis failed for ${file.name}:`, response.status, errorText);
        documentAnalysis = {
          document_type: category,
          content_description: `Document analysis failed: ${errorText}`,
          case_relevance: "Manual review required to determine relevance",
          exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
          immigration_value: "Medium",
          analysis_error: `Analysis failed: ${errorText}`
        };
      }

      // Create document object with OCR analysis results
      const newDoc = {
        id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        category: category,
        uploaded: true,
        timestamp: new Date().toISOString(),
        source: 'additional-upload',
        // Add OCR analysis results
        analysis: documentAnalysis,
        content_description: documentAnalysis.content_description,
        parties_mentioned: documentAnalysis.parties_mentioned,
        key_dates: documentAnalysis.key_dates,
        case_relevance: documentAnalysis.case_relevance,
        exhibit_description: documentAnalysis.exhibit_description,
        immigration_value: documentAnalysis.immigration_value,
        red_flags: documentAnalysis.red_flags,
        authenticity_indicators: documentAnalysis.authenticity_indicators,
        extracted_text: documentAnalysis.extracted_text
      };
      
      const updated = [...newDocuments, newDoc];
      setNewDocuments(updated);
      
    } catch (error) {
      console.error('Document analysis error:', error);
      
      // Add document with error indication
      const newDoc = {
        id: `doc_${Date.now()}_${Math.floor(performance.now() * 1000)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        category: category,
        uploaded: true,
        timestamp: new Date().toISOString(),
        source: 'additional-upload',
        analysis_error: "Analysis failed - manual review required",
        content_description: `Document analysis failed for ${file.name}`,
        case_relevance: "Manual review required",
        exhibit_description: `Exhibit: ${file.name} (manual review required)`,
        immigration_value: "Unknown"
      };
      
      const updated = [...newDocuments, newDoc];
      setNewDocuments(updated);
    }
  };

  const handleContinue = () => {
    // Merge new documents with existing documents
    const allDocuments = [...(formData.documents || []), ...newDocuments];
    setFormData({ 
      ...formData, 
      documents: allDocuments,
      newDocuments: newDocuments 
    });
    onNext();
  };

  const removeDocument = (docId) => {
    setNewDocuments(newDocuments.filter(doc => doc.id !== docId));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Additional Document Upload</h3>
        <p className="text-gray-400 text-lg">Upload additional documents to address identified gaps</p>
      </div>

      {/* Gap Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {highPriorityGaps.length > 0 && (
          <div className="bg-red-900/20 border border-red-400 p-4 rounded-lg">
            <h4 className="text-red-300 font-semibold mb-3 flex items-center">
              🔴 High Priority Gaps ({highPriorityGaps.length})
            </h4>
            <ul className="space-y-1">
              {highPriorityGaps.slice(0, 3).map((gap, index) => (
                <li key={index} className="text-red-200 text-sm">
                  • {gap.category}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {mediumPriorityGaps.length > 0 && (
          <div className="bg-yellow-900/20 border border-yellow-400 p-4 rounded-lg">
            <h4 className="text-yellow-300 font-semibold mb-3 flex items-center">
              🟡 Medium Priority Gaps ({mediumPriorityGaps.length})
            </h4>
            <ul className="space-y-1">
              {mediumPriorityGaps.slice(0, 3).map((gap, index) => (
                <li key={index} className="text-yellow-200 text-sm">
                  • {gap.category}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Upload Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {uploadCategories.map((category) => (
          <div
            key={category.id}
            onClick={() => setUploadCategory(category.id)}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              uploadCategory === category.id
                ? 'border-gray-400 bg-gray-800'
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
          >
            <div className="text-center">
              <div className="text-2xl mb-2">{category.icon}</div>
              <h4 className="text-white font-medium mb-1">{category.title}</h4>
              <p className="text-gray-400 text-sm">{category.description}</p>
              <div className={`text-xs mt-2 px-2 py-1 rounded ${
                category.priority === 'high' ? 'bg-red-900/20 text-red-300' :
                category.priority === 'medium' ? 'bg-yellow-900/20 text-yellow-300' :
                'bg-blue-900/20 text-blue-300'
              }`}>
                {category.priority} priority
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* File Upload Area */}
      <div className="bg-gray-800 p-6 rounded-lg border-2 border-dashed border-gray-600">
        <div className="text-center">
          <div className="text-4xl mb-4">📁</div>
          <h4 className="text-xl font-semibold text-white mb-2">
            Upload {uploadCategories.find(cat => cat.id === uploadCategory)?.title}
          </h4>
          <p className="text-gray-400 mb-4">Add documents to strengthen your RFE response</p>
          <input
            type="file"
            multiple
            onChange={(e) => {
              Array.from(e.target.files).forEach(file => {
                handleFileUpload(uploadCategory, file);
              });
            }}
            className="hidden"
            id="additional-doc-upload"
          />
          <label
            htmlFor="additional-doc-upload"
            className="bg-gray-400 text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors cursor-pointer inline-block"
          >
            Choose Files
          </label>
        </div>
      </div>

      {/* Newly Uploaded Documents */}
      {newDocuments.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">
            Newly Added Documents ({newDocuments.length})
          </h4>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {newDocuments.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between bg-emerald-900/20 border border-emerald-400 p-3 rounded">
                <div className="flex items-center">
                  <span className="text-emerald-400 mr-3">📄</span>
                  <div>
                    <div className="text-white text-sm">{doc.name}</div>
                    <div className="text-emerald-300 text-xs">
                      {uploadCategories.find(cat => cat.id === doc.category)?.title} • 
                      {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeDocument(doc.id)}
                  className="text-red-400 hover:text-red-300 text-sm px-2 py-1"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Summary */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-4 rounded-lg">
        <h5 className="text-emerald-300 font-medium mb-2">📈 Upload Impact</h5>
        <p className="text-emerald-200 text-sm">
          {newDocuments.length > 0 
            ? `You've added ${newDocuments.length} additional document${newDocuments.length > 1 ? 's' : ''} to strengthen your case. This will improve your document matching score and address identified gaps.`
            : 'Upload additional documents to address gaps and strengthen your RFE response.'
          }
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Gap Analysis
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to Affidavit Builder →
        </button>
      </div>
    </div>
  );
};

// RFE Step 8: Affidavit Builder (Enhanced with Downloads)
const RfeStep8AffidavitBuilder = ({ formData, setFormData, onNext, onBack }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('personal');
  const [affidavitContent, setAffidavitContent] = useState('');
  const [generatedAffidavits, setGeneratedAffidavits] = useState(formData.affidavits || []);

  const downloadAffidavit = (affidavit) => {
    const element = document.createElement('a');
    const file = new Blob([affidavit.content], { type: 'application/msword' });
    element.href = URL.createObjectURL(file);
    element.download = `${affidavit.title.replace(/\s+/g, '_')}.doc`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadAllAffidavits = () => {
    if (generatedAffidavits.length === 0) return;
    
    const combinedContent = generatedAffidavits.map((affidavit, index) => 
      `${index + 1}. ${affidavit.title}\n\n${affidavit.content}\n\n${'='.repeat(50)}\n\n`
    ).join('');
    
    const element = document.createElement('a');
    const file = new Blob([combinedContent], { type: 'application/msword' });
    element.href = URL.createObjectURL(file);
    element.download = 'All_Affidavits.doc';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const templates = [
    { 
      id: 'personal', 
      title: 'Personal Declaration', 
      icon: '👤',
      subtypes: [
        { id: 'general-personal', title: 'General Personal Declaration', description: 'Comprehensive personal statement' },
        { id: 'relationship-personal', title: 'Personal Relationship Declaration', description: 'Focused on relationship details' },
        { id: 'financial-personal', title: 'Personal Financial Declaration', description: 'Personal financial circumstances' }
      ]
    },
    { 
      id: 'family', 
      title: 'Family/Friend Affidavit', 
      icon: '👥',
      subtypes: [
        { id: 'family-witness', title: 'Family Member Witness', description: 'Testimony from family members' },
        { id: 'friend-witness', title: 'Close Friend Witness', description: 'Testimony from close friends' },
        { id: 'colleague-witness', title: 'Professional Colleague', description: 'Workplace relationship testimony' },
        { id: 'neighbor-witness', title: 'Neighbor/Community Witness', description: 'Community member testimony' }
      ]
    },
    { 
      id: 'circumstances', 
      title: 'Circumstantial Explanations', 
      icon: '📝',
      subtypes: [
        { id: 'missing-docs', title: 'Missing Documentation Explanation', description: 'Why documents are unavailable' },
        { id: 'special-circumstances', title: 'Special Circumstances Explanation', description: 'Unique situation explanations' },
        { id: 'cultural-explanation', title: 'Cultural Practice Explanation', description: 'Cultural context clarification' },
        { id: 'medical-explanation', title: 'Medical Circumstances Explanation', description: 'Health-related explanations' }
      ]
    },
    { 
      id: 'timeline', 
      title: 'Timeline Clarifications', 
      icon: '📅',
      subtypes: [
        { id: 'relationship-timeline', title: 'Relationship Timeline Clarification', description: 'Detailed relationship chronology' },
        { id: 'travel-timeline', title: 'Travel Timeline Explanation', description: 'Travel dates and duration explanation' },
        { id: 'residence-timeline', title: 'Residence Timeline Clarification', description: 'Address history explanation' },
        { id: 'employment-timeline', title: 'Employment Timeline Clarification', description: 'Work history explanation' }
      ]
    },
    {
      id: 'financial',
      title: 'Financial Support Affidavits',
      icon: '💰',
      subtypes: [
        { id: 'income-declaration', title: 'Income Declaration Affidavit', description: 'Employment and income details' },
        { id: 'joint-finances', title: 'Joint Financial Affairs', description: 'Shared financial responsibilities' },
        { id: 'sponsor-support', title: 'Third-Party Sponsor Support', description: 'Additional financial sponsorship' },
        { id: 'financial-hardship', title: 'Financial Hardship Explanation', description: 'Economic difficulties explanation' }
      ]
    },
    {
      id: 'documentation',
      title: 'Document Verification',
      icon: '📄',
      subtypes: [
        { id: 'document-authenticity', title: 'Document Authenticity Verification', description: 'Confirming document legitimacy' },
        { id: 'translation-accuracy', title: 'Translation Accuracy Certification', description: 'Translation verification' },
        { id: 'document-source', title: 'Document Source Explanation', description: 'Explaining document origins' },
        { id: 'record-correction', title: 'Record Correction Explanation', description: 'Explaining record discrepancies' }
      ]
    }
  ];

  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [customInputs, setCustomInputs] = useState({});
  const [showRegenerateOptions, setShowRegenerateOptions] = useState(false);
  const [regenerationFields, setRegenerationFields] = useState({
    tone: 'formal',
    detail_level: 'comprehensive',
    focus_area: 'general',
    custom_context: ''
  });

  const generateTemplate = (templateId) => {
    const caseInfo = formData.parsedNoticeData || {};
    const templates = {
      personal: `PERSONAL DECLARATION

I, [Your Full Name], hereby declare under penalty of perjury that the following is true and correct:

CASE INFORMATION:
Case Number: ${caseInfo.caseNumber || '[Case Number]'}
Receipt Number: ${caseInfo.receiptNumber || '[Receipt Number]'}

1. RELATIONSHIP BACKGROUND
I am the [petitioner/beneficiary] in this case. My relationship with [spouse name] began on [date] when we [how you met].

2. DEVELOPMENT OF RELATIONSHIP
[Describe how your relationship developed, key milestones, engagement, marriage]

3. JOINT LIFE TOGETHER
We have lived together as a married couple since [date]. We share financial responsibilities and living arrangements at [address].

4. RESPONSE TO USCIS CONCERNS
Regarding the concerns raised in your Request for Evidence dated ${caseInfo.deadline || '[RFE Date]'}:
- [Address specific concern 1]
- [Address specific concern 2]
- [Address specific concern 3]

I declare under penalty of perjury under the laws of the United States that the foregoing is true and correct.

Date: [Date]

_________________________
[Your Signature]
[Your Printed Name]`,

      family: `AFFIDAVIT OF [WITNESS NAME]

I, [Witness Full Name], being duly sworn, depose and state:

CASE INFORMATION:
This affidavit is submitted in support of Case Number: ${caseInfo.caseNumber || '[Case Number]'}

1. PERSONAL INFORMATION
I am [age] years old and reside at [address]. I am a [relationship to couple] of [couple names].

2. KNOWLEDGE OF RELATIONSHIP
I have known [petitioner] for [duration] and [beneficiary] for [duration]. I first became aware of their relationship in [year].

3. OBSERVATIONS
Based on my personal observations, I can attest their relationship is genuine because:
- [Specific examples of interactions witnessed]
- [Social gatherings attended together]
- [Changes observed in their lives as a couple]

4. CONCLUSION
In my opinion, [couple names] have a bona fide marital relationship based on love and commitment.

I declare under penalty of perjury that the foregoing is true and correct.

Date: [Date]

_________________________
[Witness Signature]
[Witness Printed Name]`
    };
    return templates[templateId] || templates.personal;
  };

  const generateEnhancedTemplate = (templateId, subtypeId, customOptions = {}) => {
    const caseInfo = formData.parsedNoticeData || {};
    const parsedBreakdown = formData.detailedBreakdown || {};
    const tone = customOptions.tone || 'formal';
    const detailLevel = customOptions.detail_level || 'standard';
    const focusArea = customOptions.focus_area || 'general';
    const customContext = customOptions.custom_context || '';

    // Enhanced template generation based on subtype and options
    const enhancedTemplates = {
      // Personal Declaration Templates
      'general-personal': () => {
        const formalTone = tone === 'formal';
        const comprehensive = detailLevel === 'comprehensive' || detailLevel === 'extensive';
        
        return `${formalTone ? 'PERSONAL DECLARATION AFFIDAVIT' : 'Personal Statement'}

I, [Your Full Name], ${formalTone ? 'hereby declare under penalty of perjury that the following is true and correct' : 'swear that the following is true'}:

CASE INFORMATION:
Case Number: ${caseInfo.caseNumber || '[Case Number]'}
Receipt Number: ${caseInfo.receiptNumber || '[Receipt Number]'}
USCIS Office: ${caseInfo.processingOffice || '[Processing Office]'}

1. PERSONAL BACKGROUND
I am [age] years old, born on [birth date] in [birth place]. I am a ${focusArea === 'relationship' ? 'married individual seeking to establish the authenticity of my marriage' : '[legal status]'} currently residing at [current address].

${comprehensive ? `
2. RELATIONSHIP HISTORY AND DEVELOPMENT
My relationship with [spouse/partner name] began on [date] when we [detailed description of how you met]. Our relationship developed naturally over [time period] through [specific activities, shared interests, courtship details].

Key relationship milestones include:
- [First meeting date and circumstances]
- [When relationship became serious]
- [Engagement details and date]
- [Marriage ceremony details and date]
- [Post-marriage life together]

3. CURRENT LIVING SITUATION
We currently live together at [address] where we have resided since [date]. Our household includes [household composition]. We share daily responsibilities including [specific examples of shared life].
` : `
2. RELATIONSHIP OVERVIEW
I have been in a relationship with [spouse/partner name] since [date]. We were married on [marriage date] and have lived together since [cohabitation start date].
`}

4. RESPONSE TO USCIS REQUEST FOR EVIDENCE
${parsedBreakdown.mainConcerns ? 
  parsedBreakdown.mainConcerns.map((concern, index) => 
    `Concern ${index + 1}: ${concern.issue}
Response: [Address this specific concern with detailed explanation and evidence]`
  ).join('\n\n') :
  `Regarding the concerns raised in your Request for Evidence:
- [Address specific concern 1 with detailed explanation]
- [Address specific concern 2 with detailed explanation]
- [Address specific concern 3 with detailed explanation]`
}

${customContext ? `
ADDITIONAL CONTEXT:
${customContext}
` : ''}

${formalTone ? 
  'I declare under penalty of perjury under the laws of the United States that the foregoing is true and correct.' :
  'I swear that all information provided above is true and accurate to the best of my knowledge.'
}

Date: [Date]

_________________________
[Your Signature]
[Your Printed Name]`;
      },

      'relationship-personal': () => `PERSONAL RELATIONSHIP DECLARATION

I, [Your Full Name], declare under penalty of perjury that the following statement regarding my relationship is true and correct:

CASE INFORMATION:
Case Number: ${caseInfo.caseNumber || '[Case Number]'}
This declaration addresses concerns about the authenticity of my relationship with [spouse name].

1. HOW WE MET AND EARLY RELATIONSHIP
${detailLevel === 'comprehensive' ? `
We first met on [date] at [location/circumstance]. [Detailed description of first meeting, first impressions, initial interactions]. Our first conversation was about [topic], and I was immediately drawn to [specific qualities].

Our early relationship developed through:
- [Specific dates and activities during courtship]
- [How you got to know each other better]
- [When you realized this was a serious relationship]
- [Family introductions and reactions]
` : `
We met on [date] when [brief description]. Our relationship progressed naturally from [friendship/dating] to a committed partnership over [time period].
`}

2. RELATIONSHIP MILESTONES AND COMMITMENT
${detailLevel === 'extensive' ? `
Key relationship milestones that demonstrate our commitment:

Dating Period ([start date] to [end date]):
- [Specific examples of dating activities]
- [Meeting each other's families and friends]
- [Shared experiences and trips]

Engagement ([engagement date]):
- [Proposal details and circumstances]
- [Family reactions and announcements]
- [Wedding planning together]

Marriage ([marriage date]):
- [Wedding ceremony details]
- [Wedding attendees and celebration]
- [Honeymoon and post-wedding life]
` : `
We became engaged on [date] and were married on [marriage date]. Our commitment has been demonstrated through [examples of shared life decisions].
`}

3. CURRENT MARRIED LIFE
We have been living as a married couple for [duration] at [address]. Our daily life together includes:
- Shared financial responsibilities: [specific examples]
- Household duties and decisions: [specific examples]
- Social activities and friendships: [specific examples]
- Future planning: [specific examples]

${focusArea === 'financial' ? `
4. FINANCIAL INTEGRATION
Our financial lives are completely integrated:
- Joint bank accounts: [account details]
- Shared credit cards: [details]
- Joint major purchases: [examples]
- Shared financial goals: [examples]
` : ''}

I declare under penalty of perjury that this relationship is genuine and based on love and commitment, not for immigration benefits.

Date: [Date]

_________________________
[Your Signature]
[Your Printed Name]`,

      // Family/Friend Witness Templates
      'family-witness': () => `FAMILY MEMBER WITNESS AFFIDAVIT

I, [Witness Full Name], being duly sworn, depose and state:

CASE INFORMATION:
This affidavit supports the immigration case of [Couple Names]
Case Number: ${caseInfo.caseNumber || '[Case Number]'}

1. MY RELATIONSHIP TO THE COUPLE
I am [relationship - parent, sibling, etc.] of [petitioner/beneficiary name]. I am [age] years old and have resided at [address] for [duration]. I am a [citizenship status].

2. KNOWLEDGE OF THEIR RELATIONSHIP
${detailLevel === 'comprehensive' ? `
I have known about [petitioner] and [beneficiary]'s relationship since [date when first learned]. I first met [beneficiary name] on [date] when [circumstances of meeting].

My observations of their relationship development:
- [Specific examples of early relationship observations]
- [How I witnessed their relationship grow]
- [Family events they attended together]
- [Changes I observed as they became more committed]

As a family member, I have had unique opportunities to observe their relationship in intimate family settings.
` : `
I have known about their relationship since [date] and have observed them together on numerous occasions including [examples of family gatherings].
`}

3. EVIDENCE OF GENUINE RELATIONSHIP
Based on my family relationship and close observations, I can attest that their relationship is genuine because:

${tone === 'personal' ? `
- The way they look at each other and interact shows genuine affection
- They support each other through family challenges and celebrations
- They have integrated into our family naturally and comfortably
- They make decisions together as a committed couple
- They share inside jokes and have couple-specific communication patterns
` : `
- Consistent demonstration of mutual respect and affection
- Shared participation in family events and traditions
- Joint decision-making on important life matters
- Natural integration into family dynamics
- Demonstrated commitment through various life circumstances
`}

Specific examples include:
- [Detailed example 1 of genuine interaction witnessed]
- [Detailed example 2 of relationship authenticity]
- [Detailed example 3 of their commitment to each other]

4. FAMILY ACCEPTANCE AND INTEGRATION
Our family has fully accepted [beneficiary name] as a family member. [He/She] participates in [family traditions, holidays, important events]. The relationship has brought [positive changes observed in family dynamics].

I declare under penalty of perjury that based on my family relationship and close observations, [couple names] have a bona fide marriage based on love and mutual commitment.

Date: [Date]

_________________________
[Witness Signature]
[Witness Printed Name]
[Relationship to Couple]`,

      // Timeline Clarification Templates
      'relationship-timeline': () => `RELATIONSHIP TIMELINE CLARIFICATION AFFIDAVIT

I, [Your Full Name], provide this timeline clarification to address questions about my relationship chronology:

CASE INFORMATION:
Case Number: ${caseInfo.caseNumber || '[Case Number]'}
This affidavit clarifies timeline questions raised in the RFE dated ${caseInfo.deadline || '[RFE Date]'}

DETAILED RELATIONSHIP TIMELINE:

${detailLevel === 'extensive' ? `
INITIAL MEETING PERIOD:
[Date]: First meeting at [location/circumstance]
[Date]: Second meeting/date - [activity and location]
[Date]: [Additional early interactions]

DATING PERIOD:
[Date]: Became exclusive/official relationship
[Date]: First "I love you" exchange
[Date]: [Significant dating milestone]
[Date]: Met each other's families
[Date]: [Other relationship developments]

ENGAGEMENT PERIOD:
[Date]: Engagement/proposal - [details of proposal]
[Date]: Engagement announcement to families
[Date]: [Wedding planning milestones]

MARRIAGE:
[Date]: Wedding ceremony at [location]
[Date]: [Honeymoon or post-wedding activities]

POST-MARRIAGE TIMELINE:
[Date]: Established joint residence at [address]
[Date]: [Significant post-marriage milestones]
[Date]: [Current status and future plans]
` : `
Key Relationship Dates:
- [Date]: First met
- [Date]: Started dating exclusively  
- [Date]: Engagement
- [Date]: Marriage ceremony
- [Date]: Established joint residence
- [Current]: Present status
`}

CLARIFICATION OF SPECIFIC TIMELINE CONCERNS:
${parsedBreakdown.mainConcerns ? 
  parsedBreakdown.mainConcerns.filter(concern => 
    concern.issue.toLowerCase().includes('timeline') || 
    concern.issue.toLowerCase().includes('date')
  ).map(concern => `
Issue: ${concern.issue}
Clarification: [Detailed explanation addressing this specific timeline concern]
Supporting Evidence: [Reference to documents that support this timeline]
`).join('\n') :
  `[Address specific timeline discrepancies mentioned in the RFE]`
}

${customContext ? `
ADDITIONAL TIMELINE CONTEXT:
${customContext}
` : ''}

I declare under penalty of perjury that this timeline accurately reflects the development of our relationship.

Date: [Date]

_________________________
[Your Signature]
[Your Printed Name]`
    };

    // Return the appropriate enhanced template
    if (enhancedTemplates[subtypeId]) {
      return enhancedTemplates[subtypeId]();
    }
    
    // Fallback to basic template if subtype not found
    return generateTemplate(templateId);
  };

  const saveAffidavit = () => {
    if (affidavitContent.trim()) {
      const newAffidavit = {
        id: Date.now(),
        type: selectedTemplate,
        title: templates.find(t => t.id === selectedTemplate)?.title,
        content: affidavitContent,
        created: new Date().toISOString()
      };
      
      const updated = [...generatedAffidavits, newAffidavit];
      setGeneratedAffidavits(updated);
      setFormData({ ...formData, affidavits: updated });
      setAffidavitContent('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Affidavit Builder</h3>
        <p className="text-gray-400 text-lg">Create professional affidavits to support your case</p>
      </div>

      <div className="space-y-6">
        {/* Template Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div key={template.id} className="space-y-2">
              <div
                onClick={() => {
                  setSelectedTemplate(template.id);
                  setSelectedSubtype(null);
                  setShowRegenerateOptions(false);
                }}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedTemplate === template.id
                    ? 'border-blue-400 bg-blue-900/30'
                    : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                }`}
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">{template.icon}</span>
                  <h4 className="text-white font-medium">{template.title}</h4>
                </div>
                <p className="text-gray-400 text-sm">{template.subtypes.length} variations available</p>
              </div>

              {/* Subtype Selection */}
              {selectedTemplate === template.id && (
                <div className="ml-4 space-y-2">
                  {template.subtypes.map((subtype) => (
                    <div
                      key={subtype.id}
                      onClick={() => {
                        setSelectedSubtype(subtype.id);
                        setAffidavitContent(generateEnhancedTemplate(template.id, subtype.id));
                      }}
                      className={`p-3 rounded border cursor-pointer transition-all ${
                        selectedSubtype === subtype.id
                          ? 'border-green-400 bg-green-900/20'
                          : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                      }`}
                    >
                      <h5 className="text-white font-medium text-sm">{subtype.title}</h5>
                      <p className="text-gray-400 text-xs">{subtype.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Regeneration Options */}
        {selectedTemplate && selectedSubtype && (
          <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-blue-300 font-semibold">🔄 Regeneration Options</h4>
              <button
                onClick={() => setShowRegenerateOptions(!showRegenerateOptions)}
                className="text-blue-300 hover:text-blue-200 text-sm"
              >
                {showRegenerateOptions ? 'Hide Options' : 'Show Options'}
              </button>
            </div>
            
            {showRegenerateOptions && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-blue-300 text-sm font-medium mb-2">Tone</label>
                  <select
                    value={regenerationFields.tone}
                    onChange={(e) => setRegenerationFields({...regenerationFields, tone: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  >
                    <option value="formal">Formal Legal</option>
                    <option value="personal">Personal & Warm</option>
                    <option value="detailed">Detailed & Comprehensive</option>
                    <option value="concise">Concise & Direct</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-blue-300 text-sm font-medium mb-2">Detail Level</label>
                  <select
                    value={regenerationFields.detail_level}
                    onChange={(e) => setRegenerationFields({...regenerationFields, detail_level: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  >
                    <option value="basic">Basic Template</option>
                    <option value="standard">Standard Detail</option>
                    <option value="comprehensive">Comprehensive</option>
                    <option value="extensive">Extensive Detail</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-blue-300 text-sm font-medium mb-2">Focus Area</label>
                  <select
                    value={regenerationFields.focus_area}
                    onChange={(e) => setRegenerationFields({...regenerationFields, focus_area: e.target.value})}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  >
                    <option value="general">General Statement</option>
                    <option value="relationship">Relationship Focus</option>
                    <option value="financial">Financial Focus</option>
                    <option value="timeline">Timeline Focus</option>
                    <option value="documentation">Documentation Focus</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-blue-300 text-sm font-medium mb-2">Custom Context</label>
                  <input
                    type="text"
                    value={regenerationFields.custom_context}
                    onChange={(e) => setRegenerationFields({...regenerationFields, custom_context: e.target.value})}
                    placeholder="Add specific context..."
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400"
                  />
                </div>
              </div>
            )}
            
            <button
              onClick={() => {
                setAffidavitContent(generateEnhancedTemplate(selectedTemplate, selectedSubtype, regenerationFields));
              }}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
            >
              🔄 Regenerate with Options
            </button>
          </div>
        )}
      </div>

      <div className="bg-gray-800 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xl font-semibold text-white">Affidavit Content</h4>
          <button
            onClick={saveAffidavit}
            disabled={!affidavitContent.trim()}
            className={`px-4 py-2 rounded font-medium transition-colors ${
              affidavitContent.trim()
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            Save Affidavit
          </button>
        </div>
        
        <textarea
          value={affidavitContent}
          onChange={(e) => setAffidavitContent(e.target.value)}
          placeholder="Start typing or select a template..."
          rows={12}
          className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 font-mono text-sm"
        />
      </div>

      {generatedAffidavits.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-semibold text-white">Generated Affidavits ({generatedAffidavits.length})</h4>
            <button
              onClick={downloadAllAffidavits}
              className="px-4 py-2 rounded font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              📥 Download All
            </button>
          </div>
          <div className="space-y-3">
            {generatedAffidavits.map((affidavit) => (
              <div key={affidavit.id} className="flex items-center justify-between bg-gray-700 p-3 rounded">
                <div className="flex items-center">
                  <span className="text-gray-400 mr-3">📄</span>
                  <div>
                    <div className="text-white text-sm">{affidavit.title}</div>
                    <div className="text-gray-400 text-xs">
                      Created: {new Date(affidavit.created).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => downloadAffidavit(affidavit)}
                  className="px-3 py-1 rounded text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                >
                  📥 Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Additional Documents
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to Cover Letter →
        </button>
      </div>
    </div>
  );
};

// RFE Step 9: Drafting Cover Letter / Response (Enhanced with Download)
const RfeStep9CoverLetter = ({ formData, setFormData, onNext, onBack }) => {
  const [coverLetter, setCoverLetter] = useState(formData.coverLetter || '');
  const [generating, setGenerating] = useState(false);

  const downloadCoverLetter = () => {
    if (!coverLetter.trim()) return;
    
    const element = document.createElement('a');
    const file = new Blob([coverLetter], { type: 'application/msword' });
    element.href = URL.createObjectURL(file);
    element.download = 'RFE_Response_Cover_Letter.doc';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generateCoverLetter = () => {
    setGenerating(true);
    
    setTimeout(() => {
      const caseInfo = formData.parsedNoticeData || {};
      const concerns = formData.detailedBreakdown?.mainConcerns || [];
      const analysisResults = formData.analysisResults || {};
      
      const generatedLetter = `[Date]

U.S. Citizenship and Immigration Services
${caseInfo.processingOffice || 'Processing Office'}

RE: Response to Request for Evidence
    Case Number: ${caseInfo.caseNumber || '[Case Number]'}
    Receipt Number: ${caseInfo.receiptNumber || '[Receipt Number]'}
    RFE Date: ${caseInfo.deadline || '[RFE Date]'}

Dear USCIS Officer,

This letter serves as our comprehensive response to the Request for Evidence dated ${caseInfo.deadline || '[RFE Date]'} regarding the above-referenced case.

EXECUTIVE SUMMARY
We have carefully reviewed your notice and have addressed each concern raised with substantial additional evidence. The enclosed documentation demonstrates the bona fide nature of the relationship and satisfies all requirements under applicable law.

RESPONSE TO SPECIFIC CONCERNS
${concerns.map((concern, index) => `
${index + 1}. ${concern.issue.toUpperCase()}
Your office noted: "${concern.description}"

In response, we have provided:
${concern.actionItems.map((item, itemIndex) => `   ${String.fromCharCode(97 + itemIndex)}. ${item}`).join('\n')}

This evidence directly addresses your concerns and demonstrates [specific legal standard].
`).join('\n')}

EVIDENCE ANALYSIS SUMMARY
Our comprehensive document review reveals:
- Document Matching: ${analysisResults.documentMatching?.overallScore || 'Strong'} alignment with USCIS requests
- Consistency Analysis: Names, dates, and addresses have been verified for consistency
- Temporal Coverage: ${analysisResults.temporalCoverage?.coveragePercentage || '85'}% coverage of relationship timeline
- Document Quality: All submissions are clear, authenticated, and properly formatted

EXHIBITS SUBMITTED
The enclosed documentation includes:
• Exhibit A: Enhanced Financial Documentation
• Exhibit B: Cohabitation Evidence Package  
• Exhibit C: Relationship Timeline Documentation
• Exhibit D: Third-Party Affidavits and Statements
• Exhibit E: Additional Supporting Evidence

LEGAL ARGUMENT
Based on the Matter of [Relevant Case Law] and 8 CFR [Relevant Regulation], the submitted evidence clearly establishes [legal conclusion]. The documentation provided exceeds the evidentiary standards set forth in the applicable regulations and demonstrates that all requirements have been satisfied.

CONCLUSION
We respectfully submit that the evidence presented in this response package comprehensively addresses all concerns raised in your Request for Evidence. The documentation clearly demonstrates the bona fide nature of the relationship and satisfies all applicable legal requirements.

We respectfully request that USCIS approve the pending petition based on the substantial evidence provided. Should you require any additional information or clarification, please do not hesitate to contact us.

Thank you for your careful consideration of this response.

Respectfully submitted,

[Your Name]
[Your Title/Relationship to Case]
[Contact Information]

Enclosures: Evidence exhibits as described above

cc: [Attorney if applicable]`;

      setCoverLetter(generatedLetter);
      setFormData({ ...formData, coverLetter: generatedLetter });
      setGenerating(false);
    }, 2000);
  };

  React.useEffect(() => {
    if (!coverLetter) {
      generateCoverLetter();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Cover Letter & Response</h3>
        <p className="text-gray-400 text-lg">Professional legal response to USCIS with comprehensive formatting</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Cover Letter</h4>
          <p className="text-gray-400">Creating professional legal response based on your case details...</p>
        </div>
      )}

      {!generating && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-semibold text-white">Response Letter</h4>
            <div className="flex gap-3">
              <button
                onClick={downloadCoverLetter}
                disabled={!coverLetter.trim()}
                className={`px-4 py-2 rounded font-medium transition-colors ${
                  coverLetter.trim()
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                📥 Download Letter
              </button>
              <button
                onClick={generateCoverLetter}
                className="px-4 py-2 rounded font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                🔄 Regenerate
              </button>
            </div>
          </div>
          
          <textarea
            value={coverLetter}
            onChange={(e) => {
              setCoverLetter(e.target.value);
              setFormData({ ...formData, coverLetter: e.target.value });
            }}
            rows={20}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 font-mono text-sm"
          />
          
          <div className="mt-4 text-xs text-gray-400">
            📝 This cover letter includes legal formatting, case citations, and professional language appropriate for USCIS submission.
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Affidavit Builder
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to AI Review →
        </button>
      </div>
    </div>
  );
};

// RFE Step 10: AI Final Review & Checklist (Automated)
const RfeStep10AIFinalReview = ({ formData, setFormData, onNext, onBack }) => {
  const [reviewing, setReviewing] = useState(false);
  const [reviewResults, setReviewResults] = useState(formData.finalReview || {});

  const performAIReview = () => {
    setReviewing(true);
    
    setTimeout(() => {
      // Dynamic analysis based on actual user data
      const totalDocuments = (formData.documents || []).length + (formData.newDocuments || []).length;
      const affidavitCount = (formData.affidavits || []).length;
      const hasCoverLetter = formData.coverLetter && formData.coverLetter.trim().length > 100;
      const mainConcerns = formData.detailedBreakdown?.mainConcerns || [];
      const analysisResults = formData.analysisResults || {};
      
      // Dynamic checklist based on actual case data
      const checklist = [
        { 
          id: 'uscis_items', 
          text: 'All USCIS items addressed', 
          status: mainConcerns.length > 0 && totalDocuments >= mainConcerns.length ? 'pass' : 'fail', 
          details: mainConcerns.length > 0 
            ? `${mainConcerns.length} main concerns identified, ${totalDocuments} documents provided to address them`
            : 'No USCIS concerns were analyzed from your uploaded notice'
        },
        { 
          id: 'cover_letter', 
          text: 'Cover letter present', 
          status: hasCoverLetter ? 'pass' : 'fail', 
          details: hasCoverLetter 
            ? `Professional cover letter generated (${Math.round(formData.coverLetter.length / 100)} paragraphs)`
            : 'No cover letter has been generated yet'
        },
        { 
          id: 'documents_organized', 
          text: 'Documents uploaded', 
          status: totalDocuments >= 3 ? 'pass' : totalDocuments >= 1 ? 'warning' : 'fail', 
          details: `${totalDocuments} document${totalDocuments !== 1 ? 's' : ''} uploaded total`
        },
        { 
          id: 'affidavits_present', 
          text: 'Affidavits included', 
          status: affidavitCount >= 2 ? 'pass' : affidavitCount >= 1 ? 'warning' : 'fail', 
          details: affidavitCount > 0 
            ? `${affidavitCount} affidavit${affidavitCount !== 1 ? 's' : ''} created`
            : 'No affidavits have been created yet'
        },
        { 
          id: 'deadline_compliance', 
          text: 'Deadline compliance', 
          status: formData.deadline ? 'pass' : 'warning', 
          details: formData.deadline 
            ? `Response deadline: ${formData.deadline}`
            : 'No deadline information available from uploaded notice'
        },
        { 
          id: 'evidence_quality', 
          text: 'Evidence analysis completed', 
          status: analysisResults.documentMatching ? 'pass' : 'warning', 
          details: analysisResults.documentMatching 
            ? `Document matching analysis shows ${analysisResults.documentMatching.overallScore} alignment with USCIS requests`
            : 'Document analysis has not been completed'
        },
        { 
          id: 'gap_analysis', 
          text: 'Evidence gaps addressed', 
          status: formData.gapAnalysis?.gaps ? 
            (formData.newDocuments && formData.newDocuments.length > 0 ? 'pass' : 'warning') : 'fail', 
          details: formData.gapAnalysis?.gaps 
            ? `${formData.gapAnalysis.gaps.length} gap${formData.gapAnalysis.gaps.length !== 1 ? 's' : ''} identified, ${(formData.newDocuments || []).length} additional document${(formData.newDocuments || []).length !== 1 ? 's' : ''} uploaded to address them`
            : 'Gap analysis has not been completed'
        },
        { 
          id: 'response_completeness', 
          text: 'Response package complete', 
          status: (hasCoverLetter && totalDocuments >= 3 && affidavitCount >= 1) ? 'pass' : 'warning', 
          details: `Cover letter: ${hasCoverLetter ? '✓' : '✗'}, Documents: ${totalDocuments}, Affidavits: ${affidavitCount}`
        }
      ];

      const passedItems = checklist.filter(item => item.status === 'pass').length;
      const warningItems = checklist.filter(item => item.status === 'warning').length;
      const failedItems = checklist.filter(item => item.status === 'fail').length;

      const overallStatus = failedItems > 0 ? 'needs_work' : 
                           warningItems > 0 ? 'good_with_warnings' : 'excellent';

      const results = {
        checklist,
        summary: {
          passed: passedItems,
          warnings: warningItems,
          failed: failedItems,
          total: checklist.length,
          overallStatus,
          completionScore: Math.round((passedItems / checklist.length) * 100)
        },
        recommendations: [],
        readyForSubmission: failedItems === 0
      };

      // Dynamic recommendations based on actual issues
      if (!hasCoverLetter) {
        results.recommendations.push('Generate a professional cover letter in Step 9');
      }
      if (totalDocuments < 3) {
        results.recommendations.push('Upload more supporting documents to strengthen your evidence');
      }
      if (affidavitCount === 0) {
        results.recommendations.push('Create at least 1-2 affidavits to support your case');
      }
      if (!analysisResults.documentMatching) {
        results.recommendations.push('Complete the document analysis in Step 5 to verify evidence alignment');
      }
      if (failedItems > 0) {
        results.recommendations.push('Address all failed checklist items before submitting to USCIS');
      }

      setReviewResults(results);
      setFormData({ ...formData, finalReview: results });
      setReviewing(false);
    }, 4000);
  };

  React.useEffect(() => {
    if (Object.keys(reviewResults).length === 0) {
      performAIReview();
    }
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return '✅';
      case 'warning': return '⚠️';
      case 'fail': return '❌';
      default: return '⚪';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass': return 'text-emerald-400';
      case 'warning': return 'text-yellow-400';
      case 'fail': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getOverallStatusDisplay = (status) => {
    switch (status) {
      case 'excellent': 
        return { 
          icon: '🎉', 
          text: 'Excellent - Ready for Submission', 
          color: 'text-emerald-400',
          bg: 'bg-emerald-900/20 border-emerald-400'
        };
      case 'good_with_warnings': 
        return { 
          icon: '⚠️', 
          text: 'Good - Minor Improvements Suggested', 
          color: 'text-yellow-400',
          bg: 'bg-yellow-900/20 border-yellow-400'
        };
      case 'needs_work': 
        return { 
          icon: '❌', 
          text: 'Needs Work - Issues Must Be Addressed', 
          color: 'text-red-400',
          bg: 'bg-red-900/20 border-red-400'
        };
      default: 
        return { 
          icon: '⚪', 
          text: 'Under Review', 
          color: 'text-gray-400',
          bg: 'bg-gray-900/20 border-gray-400'
        };
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">AI Final Review & Checklist</h3>
        <p className="text-gray-400 text-lg">Automated comprehensive review of your response package</p>
      </div>

      {reviewing && (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">AI Review in Progress</h4>
          <p className="text-gray-400">Our AI is performing comprehensive quality checks on your response package...</p>
          <div className="mt-4 text-sm text-gray-500">
            Checking document completeness, consistency, legal compliance, and formatting standards
          </div>
        </div>
      )}

      {/* Proceed Regardless Message */}
      {!reviewing && reviewResults.summary && (
        <div className="bg-gray-700/50 border border-gray-600 p-4 rounded-lg">
          <div className="flex items-center">
            <span className="text-blue-400 mr-2">ℹ️</span>
            <p className="text-gray-300 text-sm">
              <strong>You can proceed to Quality Assurance regardless of your current score.</strong> 
              The review is for guidance only - you have the final decision on when your case is ready for submission.
            </p>
          </div>
        </div>
      )}

      {!reviewing && reviewResults.summary && (
        <>
          {/* Overall Status */}
          <div className={`p-6 rounded-lg border ${getOverallStatusDisplay(reviewResults.summary.overallStatus).bg}`}>
            <div className="text-center">
              <div className="text-4xl mb-3">{getOverallStatusDisplay(reviewResults.summary.overallStatus).icon}</div>
              <div className={`text-2xl font-bold mb-2 ${getOverallStatusDisplay(reviewResults.summary.overallStatus).color}`}>
                {getOverallStatusDisplay(reviewResults.summary.overallStatus).text}
              </div>
              <div className="text-lg text-gray-300 mb-4">
                Completion Score: {reviewResults.summary.completionScore}%
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-emerald-400">{reviewResults.summary.passed}</div>
                  <div className="text-xs text-gray-400">Passed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-400">{reviewResults.summary.warnings}</div>
                  <div className="text-xs text-gray-400">Warnings</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-400">{reviewResults.summary.failed}</div>
                  <div className="text-xs text-gray-400">Failed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Checklist */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📋 AI Review Checklist</h4>
            <div className="space-y-3">
              {reviewResults.checklist?.map((item, index) => (
                <div key={index} className="flex items-start justify-between bg-gray-700 p-4 rounded">
                  <div className="flex items-start flex-1">
                    <span className="text-xl mr-3">{getStatusIcon(item.status)}</span>
                    <div>
                      <div className={`font-medium ${getStatusColor(item.status)}`}>
                        {item.text}
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        {item.details}
                      </div>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    item.status === 'pass' ? 'bg-emerald-900/20 text-emerald-300' :
                    item.status === 'warning' ? 'bg-yellow-900/20 text-yellow-300' :
                    'bg-red-900/20 text-red-300'
                  }`}>
                    {item.status.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Recommendations */}
          {reviewResults.recommendations && reviewResults.recommendations.length > 0 && (
            <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
              <h4 className="text-blue-300 font-semibold mb-4">🤖 AI Recommendations</h4>
              <ul className="space-y-2">
                {reviewResults.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start text-blue-200">
                    <span className="text-blue-400 mr-2">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Response Package Summary */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📊 Package Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400 mb-1">
                  {(formData.documents?.length || 0) + (formData.newDocuments?.length || 0)}
                </div>
                <div className="text-gray-400 text-sm">Total Documents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-400 mb-1">{formData.affidavits?.length || 0}</div>
                <div className="text-gray-400 text-sm">Affidavits</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400 mb-1">
                  {formData.detailedBreakdown?.mainConcerns?.length || 0}
                </div>
                <div className="text-gray-400 text-sm">Concerns Addressed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400 mb-1">1</div>
                <div className="text-gray-400 text-sm">Cover Letter</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Cover Letter
        </button>
        <button
          onClick={onNext}
          disabled={reviewing}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !reviewing
              ? 'bg-gray-400 text-black hover:bg-gray-300'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Quality Assurance →
        </button>
      </div>
    </div>
  );
};

// RFE Step 11: Final Quality Assurance
const RfeStep11QualityAssurance = ({ formData, setFormData, onNext, onBack }) => {
  const [qaResults, setQaResults] = useState(formData.qualityAssurance || {});
  const [reviewing, setReviewing] = useState(false);

  const performQA = () => {
    setReviewing(true);
    
    setTimeout(() => {
      // Dynamic analysis based on actual user data
      const totalDocuments = (formData.documents || []).length + (formData.newDocuments || []).length;
      const affidavitCount = (formData.affidavits || []).length;
      const hasCoverLetter = formData.coverLetter && formData.coverLetter.trim().length > 100;
      const mainConcerns = formData.detailedBreakdown?.mainConcerns || [];
      const analysisResults = formData.analysisResults || {};
      const gapAnalysis = formData.gapAnalysis || {};
      
      // Calculate dynamic scores based on actual case data
      let overallCaseStrength = 5; // Base score
      let evidenceScore = 'Weak';
      let legalArgumentStrength = 5;
      const weakPoints = [];
      
      // Document Coverage Analysis
      if (totalDocuments >= 5) {
        overallCaseStrength += 2;
        evidenceScore = 'Strong';
      } else if (totalDocuments >= 3) {
        overallCaseStrength += 1;
        evidenceScore = 'Moderate';
      } else {
        weakPoints.push(`Only ${totalDocuments} document${totalDocuments !== 1 ? 's' : ''} uploaded - consider adding more evidence`);
      }
      
      // Affidavit Analysis
      if (affidavitCount >= 2) {
        overallCaseStrength += 1;
        legalArgumentStrength += 2;
      } else if (affidavitCount >= 1) {
        overallCaseStrength += 0.5;
        legalArgumentStrength += 1;
      } else {
        weakPoints.push('No affidavits created - personal statements strengthen credibility');
      }
      
      // Cover Letter Analysis
      if (hasCoverLetter) {
        legalArgumentStrength += 1;
        overallCaseStrength += 1;
      } else {
        weakPoints.push('No cover letter generated - professional response letters are essential');
      }
      
      // USCIS Concerns Coverage
      if (mainConcerns.length > 0) {
        const addressedConcerns = Math.min(totalDocuments, mainConcerns.length);
        const coverageRatio = addressedConcerns / mainConcerns.length;
        overallCaseStrength += coverageRatio * 2;
        legalArgumentStrength += coverageRatio * 2;
        
        if (coverageRatio < 1) {
          weakPoints.push(`Only ${addressedConcerns}/${mainConcerns.length} USCIS concerns fully addressed with evidence`);
        }
      } else {
        weakPoints.push('USCIS notice analysis incomplete - ensure all concerns are identified');
      }
      
      // Document Quality Analysis
      if (analysisResults.documentMatching) {
        if (analysisResults.documentMatching.overallScore === 'Strong') {
          overallCaseStrength += 1;
          evidenceScore = 'Strong';
        } else if (analysisResults.documentMatching.overallScore === 'Moderate') {
          overallCaseStrength += 0.5;
          if (evidenceScore === 'Weak') evidenceScore = 'Moderate';
        } else {
          weakPoints.push('Document analysis shows weak alignment with USCIS requests');
        }
      }
      
      // Gap Analysis
      if (gapAnalysis.gaps && gapAnalysis.gaps.length > 0) {
        const highPriorityGaps = gapAnalysis.gaps.filter(gap => gap.impact === 'High').length;
        const addressedWithNewDocs = (formData.newDocuments || []).length;
        
        if (highPriorityGaps > addressedWithNewDocs) {
          weakPoints.push(`${highPriorityGaps} high-priority gaps identified, only ${addressedWithNewDocs} additional documents uploaded`);
          overallCaseStrength -= 0.5;
        }
      }
      
      // Final score adjustments
      overallCaseStrength = Math.max(1, Math.min(10, Math.round(overallCaseStrength * 10) / 10));
      legalArgumentStrength = Math.max(1, Math.min(10, Math.round(legalArgumentStrength * 10) / 10));
      
      // Determine success likelihood and risk
      let successLikelihood = 'Low';
      let riskOfFurtherRFE = 'High';
      
      if (overallCaseStrength >= 8 && evidenceScore === 'Strong') {
        successLikelihood = 'High';
        riskOfFurtherRFE = 'Low';
      } else if (overallCaseStrength >= 6 && evidenceScore !== 'Weak') {
        successLikelihood = 'Medium';
        riskOfFurtherRFE = 'Medium';
      }
      
      // Add specific recommendations based on analysis
      if (weakPoints.length === 0) {
        weakPoints.push('No significant weaknesses identified - case appears well-prepared');
      }
      
      const results = {
        overallCaseStrength,
        evidenceScore,
        legalArgumentStrength,
        complianceScore: hasCoverLetter && totalDocuments >= 3,
        weakPoints,
        successLikelihood,
        riskOfFurtherRFE,
        caseMetrics: {
          totalDocuments,
          affidavitCount,
          concernsAddressed: mainConcerns.length,
          hasCoverLetter
        }
      };
      
      setQaResults(results);
      setFormData({ ...formData, qualityAssurance: results });
      setReviewing(false);
    }, 2000);
  };

  React.useEffect(() => {
    if (Object.keys(qaResults).length === 0) {
      performQA();
    }
  }, []);

  const getScoreColor = (score) => {
    if (typeof score === 'number') {
      return score >= 8 ? 'text-emerald-400' : score >= 6 ? 'text-yellow-400' : 'text-red-400';
    }
    switch (score) {
      case 'Strong': return 'text-emerald-400';
      case 'Moderate': return 'text-yellow-400';
      case 'Weak': return 'text-red-400';
      case 'High': return 'text-emerald-400';
      case 'Medium': return 'text-yellow-400';
      case 'Low': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getFinalStatus = () => {
    if (qaResults.overallCaseStrength >= 8 && qaResults.evidenceScore === 'Strong') {
      return { status: '✅ Approved to Submit', color: 'text-emerald-400', bg: 'bg-emerald-900/20 border-emerald-400' };
    } else if (qaResults.overallCaseStrength >= 6) {
      return { status: '⚠️ Improvements Recommended', color: 'text-yellow-400', bg: 'bg-yellow-900/20 border-yellow-400' };
    } else {
      return { status: '🛑 Significant Concerns – Consider attorney', color: 'text-red-400', bg: 'bg-red-900/20 border-red-400' };
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Final Quality Assurance</h3>
        <p className="text-gray-400 text-lg">Professional review of your complete response package</p>
      </div>

      {reviewing && (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Performing Quality Assurance</h4>
          <p className="text-gray-400">Running final checks on your response package...</p>
        </div>
      )}

      {!reviewing && qaResults.overallCaseStrength && (
        <>
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📊 Quality Metrics</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center">
                <div className={`text-2xl font-bold mb-1 ${getScoreColor(qaResults.overallCaseStrength)}`}>
                  {qaResults.overallCaseStrength}/10
                </div>
                <div className="text-gray-400 text-sm">Overall Case Strength</div>
              </div>
              <div className="text-center">
                <div className={`text-lg font-bold mb-1 ${getScoreColor(qaResults.evidenceScore)}`}>
                  {qaResults.evidenceScore}
                </div>
                <div className="text-gray-400 text-sm">Evidence Score</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold mb-1 ${getScoreColor(qaResults.legalArgumentStrength)}`}>
                  {qaResults.legalArgumentStrength}/10
                </div>
                <div className="text-gray-400 text-sm">Legal Argument</div>
              </div>
              <div className="text-center">
                <div className={`text-lg font-bold mb-1 ${qaResults.complianceScore ? 'text-emerald-400' : 'text-red-400'}`}>
                  {qaResults.complianceScore ? 'Pass' : 'Fail'}
                </div>
                <div className="text-gray-400 text-sm">Compliance</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">🔍 Professional Assessment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-white font-medium mb-2">Success Likelihood</h5>
                <div className={`text-lg font-semibold ${getScoreColor(qaResults.successLikelihood)}`}>
                  {qaResults.successLikelihood}
                </div>
              </div>
              <div>
                <h5 className="text-white font-medium mb-2">Risk of Further RFE</h5>
                <div className={`text-lg font-semibold ${getScoreColor(qaResults.riskOfFurtherRFE)}`}>
                  {qaResults.riskOfFurtherRFE}
                </div>
              </div>
            </div>
          </div>

          {qaResults.weakPoints && qaResults.weakPoints.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
              <h4 className="text-yellow-300 font-semibold mb-4">⚠️ Suggested Improvements</h4>
              <ul className="space-y-2">
                {qaResults.weakPoints.map((point, index) => (
                  <li key={index} className="flex items-start text-yellow-200">
                    <span className="text-yellow-400 mr-2">•</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={`p-6 rounded-lg border ${getFinalStatus().bg}`}>
            <div className="text-center">
              <div className={`text-2xl font-bold mb-2 ${getFinalStatus().color}`}>
                {getFinalStatus().status}
              </div>
              <p className="text-gray-300">
                Your response package has been thoroughly reviewed and is ready for submission.
              </p>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Final Review
        </button>
        <button
          onClick={onNext}
          disabled={reviewing}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !reviewing
              ? 'bg-gray-400 text-black hover:bg-gray-300'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Generate Response Packet →
        </button>
      </div>
    </div>
  );
};

// RFE Step 12: Generate Response Packet (Enhanced with Word Documents & Downloads)
const RfeStep12GeneratePacket = ({ formData, setFormData, onBack, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [responsePacket, setResponsePacket] = useState(formData.responsePacket || {});

  const downloadDocument = (docType, content, filename) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'application/msword' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadCombinedPacket = () => {
    const coverLetter = formData.coverLetter || '';
    const affidavits = formData.affidavits || [];
    const analysisResults = formData.analysisResults || {};
    const caseInfo = formData.parsedNoticeData || {};

    // Create comprehensive response packet
    const packetContent = `
RFE RESPONSE PACKAGE
Case Number: ${caseInfo.caseNumber || '[Case Number]'}
Receipt Number: ${caseInfo.receiptNumber || '[Receipt Number]'}
Generated: ${new Date().toLocaleDateString()}

${'='.repeat(60)}
TABLE OF CONTENTS
${'='.repeat(60)}

1. Cover Letter & Legal Response
2. Supporting Affidavits (${affidavits.length} documents)
3. Document Analysis Summary
4. Evidence Index
5. Additional Supporting Documentation

${'='.repeat(60)}
1. COVER LETTER & LEGAL RESPONSE
${'='.repeat(60)}

${coverLetter}

${'='.repeat(60)}
2. SUPPORTING AFFIDAVITS
${'='.repeat(60)}

${affidavits.map((affidavit, index) => `
AFFIDAVIT ${index + 1}: ${affidavit.title}
${'-'.repeat(40)}
${affidavit.content}

`).join('\n')}

${'='.repeat(60)}
3. DOCUMENT ANALYSIS SUMMARY
${'='.repeat(60)}

Document Matching Analysis:
- Overall Score: ${analysisResults.documentMatching?.overallScore || 'N/A'}
- Requests Addressed: ${analysisResults.documentMatching?.addressedRequests || 0}
- Partial Addresses: ${analysisResults.documentMatching?.partiallyAddressed || 0}
- Unaddressed: ${analysisResults.documentMatching?.unaddressed || 0}

Consistency Check:
- Names: ${analysisResults.consistencyCheck?.namesConsistent ? 'Consistent' : 'Issues Found'}
- Dates: ${analysisResults.consistencyCheck?.datesConsistent ? 'Consistent' : 'Issues Found'}
- Addresses: ${analysisResults.consistencyCheck?.addressesConsistent ? 'Consistent' : 'Issues Found'}

Temporal Coverage: ${analysisResults.temporalCoverage?.coveragePercentage || 0}%

${'='.repeat(60)}
4. EVIDENCE INDEX
${'='.repeat(60)}

${(formData.documents || []).map((doc, index) => `
Exhibit ${String.fromCharCode(65 + index)}: ${doc.name}
Category: ${doc.category || 'General Evidence'}
Upload Date: ${doc.timestamp ? new Date(doc.timestamp).toLocaleDateString() : 'N/A'}
`).join('\n')}

${'='.repeat(60)}
END OF RESPONSE PACKAGE
${'='.repeat(60)}

This response package has been generated automatically and includes all supporting documentation, analysis, and legal arguments necessary to address the USCIS Request for Evidence.

For questions about this response, please refer to the contact information provided in the cover letter.
`;

    downloadDocument('complete', packetContent, 'Complete_RFE_Response_Package.doc');
  };

  const generatePacket = () => {
    setGenerating(true);
    
    setTimeout(() => {
      const packet = {
        files: [
          { 
            name: 'Cover_Letter_and_Legal_Response.doc', 
            type: 'cover-letter', 
            size: '168 KB',
            content: formData.coverLetter || '',
            downloadAction: () => downloadDocument('cover', formData.coverLetter || '', 'Cover_Letter_and_Legal_Response.doc')
          },
          { 
            name: 'Supporting_Affidavits.doc', 
            type: 'affidavits', 
            size: '89 KB',
            content: (formData.affidavits || []).map(aff => aff.content).join('\n\n'),
            downloadAction: () => {
              const affidavitContent = (formData.affidavits || []).map((aff, index) => 
                `AFFIDAVIT ${index + 1}: ${aff.title}\n\n${aff.content}\n\n${'='.repeat(50)}\n\n`
              ).join('');
              downloadDocument('affidavits', affidavitContent, 'Supporting_Affidavits.doc');
            }
          },
          { 
            name: 'Document_Analysis_Report.doc', 
            type: 'analysis', 
            size: '134 KB',
            downloadAction: () => {
              const analysis = formData.analysisResults || {};
              const analysisContent = `DOCUMENT ANALYSIS REPORT
Case Number: ${formData.parsedNoticeData?.caseNumber || '[Case Number]'}
Generated: ${new Date().toLocaleDateString()}

DOCUMENT MATCHING ANALYSIS
Overall Score: ${analysis.documentMatching?.overallScore || 'N/A'}
Requests Addressed: ${analysis.documentMatching?.addressedRequests || 0}
Partially Addressed: ${analysis.documentMatching?.partiallyAddressed || 0}
Unaddressed: ${analysis.documentMatching?.unaddressed || 0}

CONSISTENCY ANALYSIS
Names: ${analysis.consistencyCheck?.namesConsistent ? 'Consistent' : 'Issues Found'}
Dates: ${analysis.consistencyCheck?.datesConsistent ? 'Consistent' : 'Issues Found'}
Addresses: ${analysis.consistencyCheck?.addressesConsistent ? 'Consistent' : 'Issues Found'}

TEMPORAL COVERAGE
Coverage Percentage: ${analysis.temporalCoverage?.coveragePercentage || 0}%
Gaps: ${analysis.temporalCoverage?.gaps?.join(', ') || 'None identified'}

DOCUMENT QUALITY ASSESSMENT
${(analysis.documentQuality || []).map((doc, index) => 
  `${index + 1}. ${doc.name}: ${doc.rating} (${doc.issues?.join(', ') || 'No issues'})`
).join('\n')}`;
              downloadDocument('analysis', analysisContent, 'Document_Analysis_Report.doc');
            }
          },
          { 
            name: 'Evidence_Index_and_Timeline.doc', 
            type: 'index', 
            size: '45 KB',
            downloadAction: () => {
              const indexContent = `EVIDENCE INDEX AND TIMELINE
Case Number: ${formData.parsedNoticeData?.caseNumber || '[Case Number]'}
Generated: ${new Date().toLocaleDateString()}

EVIDENCE EXHIBITS
${(formData.documents || []).map((doc, index) => 
  `Exhibit ${String.fromCharCode(65 + index)}: ${doc.name}
Category: ${doc.category || 'General Evidence'}
Upload Date: ${doc.timestamp ? new Date(doc.timestamp).toLocaleDateString() : 'N/A'}
Size: ${((doc.size || 0) / 1024 / 1024).toFixed(2)} MB
`).join('\n')}

ADDITIONAL DOCUMENTS
${(formData.newDocuments || []).map((doc, index) => 
  `Additional Doc ${index + 1}: ${doc.name}
Category: ${doc.category || 'Additional Evidence'}
Upload Date: ${doc.timestamp ? new Date(doc.timestamp).toLocaleDateString() : 'N/A'}
`).join('\n')}

AFFIDAVITS INCLUDED
${(formData.affidavits || []).map((aff, index) => 
  `Affidavit ${index + 1}: ${aff.title}
Created: ${aff.created ? new Date(aff.created).toLocaleDateString() : 'N/A'}
`).join('\n')}`;
              downloadDocument('index', indexContent, 'Evidence_Index_and_Timeline.doc');
            }
          }
        ],
        formats: ['Individual Word Documents', 'Complete Package (Word)', 'Organized ZIP Archive'],
        totalSize: '436 KB',
        generatedAt: new Date().toISOString(),
        documentCount: (formData.documents?.length || 0) + (formData.newDocuments?.length || 0),
        affidavitCount: formData.affidavits?.length || 0
      };
      
      setResponsePacket(packet);
      setFormData({ ...formData, responsePacket: packet });
      setGenerating(false);
    }, 3000);
  };

  React.useEffect(() => {
    if (Object.keys(responsePacket).length === 0) {
      generatePacket();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Generate Response Packet</h3>
        <p className="text-gray-400 text-lg">Your complete RFE response in professional Word documents</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Response Packet</h4>
          <p className="text-gray-400">Creating professional Word documents formatted for USCIS submission...</p>
          <div className="mt-4 text-sm text-gray-500">
            Formatting documents, organizing exhibits, and preparing download packages
          </div>
        </div>
      )}

      {!generating && responsePacket.files && (
        <>
          <div className="bg-emerald-900/20 border border-emerald-400 p-8 rounded-lg text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-3xl font-bold text-emerald-300 mb-4">Response Packet Ready!</h3>
            <p className="text-emerald-200 text-lg mb-6">
              Your comprehensive RFE response has been generated in professional Word format and is ready for USCIS submission.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-emerald-300 font-medium">
              <div>Total Size: {responsePacket.totalSize}</div>
              <div>Documents: {responsePacket.documentCount}</div>
              <div>Affidavits: {responsePacket.affidavitCount}</div>
            </div>
          </div>

          {/* Individual Document Downloads */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📁 Individual Documents</h4>
            <div className="space-y-3">
              {responsePacket.files.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700 p-4 rounded">
                  <div className="flex items-center">
                    <span className="text-blue-400 mr-3">📄</span>
                    <div>
                      <div className="text-white text-sm font-medium">{file.name}</div>
                      <div className="text-gray-400 text-xs capitalize">
                        {file.type.replace('-', ' ')} • {file.size}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={file.downloadAction}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded font-medium text-white transition-colors"
                  >
                    📥 Download
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Package Download Options */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📦 Complete Package Downloads</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={downloadCombinedPacket}
                className="p-6 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-medium transition-colors"
              >
                <div className="text-2xl mb-2">📄</div>
                <div className="font-semibold">Complete Package (Word)</div>
                <div className="text-sm opacity-90">All documents in one Word file</div>
              </button>
              <button
                onClick={() => {
                  // Download all files individually in sequence
                  responsePacket.files.forEach((file, index) => {
                    setTimeout(() => file.downloadAction(), index * 500);
                  });
                }}
                className="p-6 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-medium transition-colors"
              >
                <div className="text-2xl mb-2">📁</div>
                <div className="font-semibold">All Files Separately</div>
                <div className="text-sm opacity-90">Download each document individually</div>
              </button>
            </div>
          </div>

          {/* Submission Instructions */}
          <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
            <h4 className="text-blue-300 font-semibold mb-4">📮 Submission Instructions</h4>
            <div className="space-y-3 text-blue-200">
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">1.</span>
                <span>Download your preferred package format above</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">2.</span>
                <span>Print all documents on standard 8.5" x 11" paper</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">3.</span>
                <span>Review all documents one final time for accuracy</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">4.</span>
                <span>Submit to USCIS before deadline: <strong>{formData.deadline}</strong></span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">5.</span>
                <span>Keep copies of all submitted materials for your records</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">6.</span>
                <span>Consider certified mail with return receipt for tracking</span>
              </div>
            </div>
          </div>

          {/* Final Notice */}
          <div className="bg-yellow-900/20 border border-yellow-400 p-4 rounded-lg">
            <h5 className="text-yellow-300 font-medium mb-2">⚠️ Important Notice</h5>
            <p className="text-yellow-200 text-sm">
              These documents are generated automatically based on your input. Please review all content carefully before submission. 
              Consider having an immigration attorney review your response before filing with USCIS.
            </p>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Quality Assurance
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Complete & Close
        </button>
      </div>
    </div>
  );
};